<template>
	<div class="live-map-container">
		<!-- Map Container -->
		<div ref="mapContainer" id="map" class="map-container"></div>

		<!-- Weather Integration: Hybrid (Windy + OWM) -->
		<!-- Windy for Forecast Layers (Wind, Temp, Pressure) -->
		<WindyLayer
			ref="windyLayerRef"
			v-if="showWindy && useWindyProvider"
			:devices="devices"
			:zoom="currentZoom"
			:center="map ? map.getCenter() : undefined"
			:layer="windyLayer"
		/>

		<!-- OWM for Real-time Layers (Rain, Clouds) -->
		<OpenWeatherLayer
			v-if="showWindy && !useWindyProvider && map"
			:map="map"
			:layer="windyLayer"
		/>

		<!-- TomTom Traffic Layer -->
		<TrafficLayer v-if="showTraffic && map" :map="map" />

		<!-- Sun Tracker -->
		<SunTracker
			ref="sunTrackerRef"
			:active="showSunTracker"
			:map="map"
			:date="wmsGlobalDate ? new Date(wmsGlobalDate) : new Date()"
			@close="showSunTracker = false"
		/>

		<!-- Charging Stations -->
		<ChargeMap ref="chargeMapRef" :active="showChargeMap" :map="map" />

		<div v-if="loading" class="loading-overlay">Caricamento mappa...</div>

		<!-- Zoom Warning (Button) -->
		<button
			v-if="showZoomWarning"
			class="zoom-warning-btn"
			@click="zoomToSatellite"
		>
			<span>🔍 Zooma sulla mappa per vedere i dati satellitari</span>
		</button>

		<!-- Timeline Control Overlay (Only if active track) -->
		<div v-if="activeTrack && activeTrack.length > 0" class="timeline-overlay">
			<!-- Stats Info Block (NEW) -->
			<div class="timeline-info-row">
				<div class="stat-col">
					<span class="stat-label">Punti tracciamento:</span>
					<span class="stat-value">{{ routeStats.points }}</span>
				</div>
				<div class="stat-col center">
					<!-- Center: Map Controls -->
					<button class="lock-btn" @click="fitTrack" title="Adatta alla rotta">
						🔍
					</button>
					<button
						class="lock-btn"
						:class="{ active: isSatelliteView }"
						@click="toggleBaseLayer"
						title="Cambia Mappa (Satellite/Strada)"
					>
						🛰️
					</button>
					<button
						class="lock-btn"
						:class="{ active: isFollowing }"
						@click="handleTimelineLock"
						:title="isFollowing ? 'Smetti di seguire' : 'Centra e Segui'"
					>
						{{ isFollowing ? "🔒" : "🎯" }}
					</button>
					<button
						class="lock-btn"
						@click="copyShareLink"
						title="Copia Link Condivisibile"
						style="margin-left: 40px"
					>
						🔗
					</button>
				</div>

				<div class="stat-col right">
					<div class="stat-group">
						<span class="stat-label">Dist:</span>
						<span class="stat-value">{{ routeStats.distance }}</span>
					</div>
					<div class="stat-group desktop-only">
						<span class="stat-label">Ultima:</span>
						<span class="stat-value">{{ routeStats.lastPos }}</span>
					</div>
				</div>
			</div>

			<!-- Header / Info -->
			<div class="timeline-header">
				<div class="timeline-meta">
					<button
						class="calendar-btn"
						@click="toggleCalendar"
						title="Seleziona data"
					>
						📅
					</button>
					<span>{{
						activeTrack && activeTrack[playbackIndex]
							? formatTimestamp(activeTrack[playbackIndex].timestamp)
							: "--:--"
					}}</span>
				</div>

				<!-- Info Stats -->
				<div class="timeline-stats">
					<span
						>{{
							((activeTrack && activeTrack[playbackIndex]?.speed) || 0).toFixed(
								0,
							)
						}}
						km/h</span
					>
				</div>

				<button
					class="close-track-btn"
					@click="$emit('close-track')"
					title="Chiudi tracciato"
				>
					❌
				</button>

				<!-- Calendar Popover -->
				<div
					v-if="showCalendar"
					class="calendar-popover"
					ref="calendarContainer"
				>
					<CalendarWidget
						:modelValue="currentDateStr"
						:activityDates="activityDates"
						@update:modelValue="onCalendarSelect"
						@close="showCalendar = false"
						:loading="loadingCalendar"
						@month-change="handleCalendarMonthChange"
					/>
				</div>
			</div>

			<!-- Timeline Visual -->
			<div class="timeline-visual-wrapper">
				<!-- Gaps -->
				<div class="timeline-gaps">
					<div
						v-for="(gap, idx) in timelineGaps"
						:key="`gap-${idx}`"
						class="gap-marker"
						:style="{ left: gap.left + '%' }"
						:title="`Gap: ${gap.duration}`"
					>
						<div class="gap-line"></div>
						<span class="gap-label">{{ gap.duration }}</span>
					</div>
				</div>

				<!-- Refuels -->
				<div class="timeline-refuels">
					<div
						v-for="(refuel, idx) in timelineRefuels"
						:key="`refuel-${idx}`"
						class="refuel-marker"
						:style="{ left: refuel.left + '%' }"
						:title="`Rifornimento: ${refuel.amount}`"
					>
						<div class="refuel-icon">⛽</div>
						<span class="refuel-label">{{ refuel.amount }}</span>
					</div>
				</div>

				<!-- Segments Bar -->
				<div class="timeline-bar">
					<div
						v-for="(seg, idx) in timelineSegments"
						:key="`seg-${idx}`"
						class="status-segment"
						:class="seg.type"
						:style="{ width: seg.width + '%' }"
					></div>
				</div>

				<!-- Ruler -->
				<div class="timeline-ruler">
					<div
						v-for="(tick, idx) in timelineTicks"
						:key="`tick-${idx}`"
						class="ruler-tick"
						:style="{ left: tick.left + '%' }"
					>
						<span class="tick-time">{{ tick.time }}</span>
						<div class="tick-mark"></div>
						<span class="tick-dist">{{ tick.distance }}</span>
					</div>
				</div>

				<!-- Slider -->
				<input
					type="range"
					min="0"
					:max="activeTrack.length - 1"
					v-model.number="playbackIndex"
					class="timeline-slider"
					@input="onSliderInput"
				/>
			</div>

			<!-- Controls -->
			<div class="playback-controls">
				<button @click="restartPlayback" class="control-btn" title="Restart">
					🔄
				</button>
				<button
					@click="togglePlayback"
					class="control-btn play-btn"
					:class="{ playing: isPlaying }"
					:title="isPlaying ? 'Pause' : 'Play'"
				>
					{{ isPlaying ? "⏸" : "▶" }}
				</button>
				<button @click="cycleSpeed" class="control-btn speed-btn" title="Speed">
					x{{ playbackSpeed }}
				</button>
			</div>
		</div>

		<!-- Maps Controls Overlay (Only Share Button, others in timeline or hidden) -->
		<!-- Maps Controls Overlay (Restored top-left) -->
		<div class="map-controls-overlay">
			<button
				class="map-control-btn"
				:class="{ active: showNDVI }"
				@click="toggleNDVI"
				title="Livelli Satellite"
			>
				🛰️
			</button>

			<!-- Windy Group -->
			<div class="control-group-column">
				<button
					class="map-control-btn"
					:class="{ active: showWindy }"
					@click="toggleWindy"
					title="Meteo (OpenWeather)"
				>
					🌪️
				</button>
				<div v-if="showWindy" class="windy-layer-selector">
					<button
						class="layer-btn"
						:class="{ active: windyLayer === 'wind' }"
						@click="windyLayer = 'wind'"
						title="Vento (Windy)"
					>
						🌬️
					</button>
					<button
						class="layer-btn"
						:class="{ active: windyLayer === 'temp' }"
						@click="windyLayer = 'temp'"
						title="Temperatura (Windy)"
					>
						🌡️
					</button>
					<button
						class="layer-btn"
						:class="{ active: windyLayer === 'pressure' }"
						@click="windyLayer = 'pressure'"
						title="Pressione atmosferica (Windy)"
					>
						🌀
					</button>
					<hr class="layer-separator" />
					<button
						class="layer-btn"
						:class="{ active: windyLayer === 'precipitation_new' }"
						@click="windyLayer = 'precipitation_new'"
						title="Pioggia (Real-time)"
					>
						☔
					</button>
					<button
						class="layer-btn"
						:class="{ active: windyLayer === 'clouds_new' }"
						@click="windyLayer = 'clouds_new'"
						title="Nuvole (Real-time)"
					>
						☁️
					</button>
				</div>
			</div>

			<button
				class="map-control-btn"
				:class="{ active: showTraffic }"
				@click="showTraffic = !showTraffic"
				title="Traffico"
			>
				🚦
			</button>

			<button
				class="map-control-btn"
				@click="copyShareLink"
				title="Copia Link Condivisibile"
			>
				🔗
			</button>

			<button
				class="map-control-btn"
				:class="{ active: showSunTracker }"
				@click="showSunTracker = !showSunTracker"
				title="Posizione Sole"
			>
				☀️
			</button>

			<button
				class="map-control-btn"
				:class="{ active: showChargeMap }"
				@click="showChargeMap = !showChargeMap"
				title="Stazioni di Ricarica"
			>
				⚡
			</button>

			<button
				class="map-control-btn"
				:class="{ active: showTrafficDensity }"
				@click="showTrafficDensity = !showTrafficDensity"
				title="Analisi Densità Traffico"
			>
				📊
			</button>

			<!-- Copertura Radio (OpenCellID) -->
			<div class="control-group-column">
				<button
					class="map-control-btn"
					:class="{ active: showCoverage }"
					@click="toggleCoverage"
					title="Copertura segnale radio (zone mal coperte)"
				>
					📶
				</button>
				<div v-if="showCoverage" class="coverage-legend">
					<div class="coverage-legend-title">Copertura {{ coverageRadio }}</div>
					<div class="coverage-legend-row"><span class="cov-swatch cov-dead"></span> Zona morta</div>
					<div class="coverage-legend-row"><span class="cov-swatch cov-weak"></span> Segnale debole</div>
					<div class="coverage-legend-hint" v-if="coverageLoading">calcolo…</div>
					<div class="coverage-legend-hint" v-else>{{ coverageMode === 'live' ? 'live (area visibile)' : 'panoramica Italia' }} · dato indicativo</div>
				</div>
			</div>

			<!-- Rete Ferroviaria (OSM) — sc-19262 -->
			<button
				class="map-control-btn"
				:class="{ active: showRailway }"
				@click="toggleRailway"
				title="Rete ferroviaria italiana"
			>
				🚆
			</button>

			<!-- Traffic Density Clear Button -->
			<button
				v-if="trafficDensityLayer"
				class="traffic-clear-btn"
				@click="clearTrafficDensityLayer"
				title="Chiudi il livello"
			>
				✕
			</button>
			<MapSearch @select-location="onMapSearchSelect" />
		</div>

		<!-- Timelapse Controls -->
		<TimelapseControls
			v-if="showTimelapse && activeSatelliteLayer"
			:isPlaying="isTimelapsePlaying"
			:currentDate="activeSatelliteLayer.date || 'Latest'"
			:availableDates="availableDatesMap[activeSatelliteLayer.id] || []"
			@toggle-play="toggleTimelapse"
			@next="nextTimelapseFrame"
			@prev="prevTimelapseFrame"
			@seek="onTimelapseSeek"
		/>

		<!-- Zoom Level Indicator (Next to standard controls) -->
		<div class="zoom-level-indicator">Zoom: {{ Math.round(currentZoom) }}</div>

		<!-- Layer Manager -->
		<LayerManager
			v-if="showLayerManager"
			:layers="availableLayers"
			v-model:activeLayers="activeLayers"
			:wmsDate="wmsGlobalDate"
			:availableDatesMap="availableDatesMap"
			@update:wmsDate="onLayerDateChange"
			:loading="loadingLayers"
			@check-availability="
				(d) => fetchAvailableDates(d.year, d.month, d.layerId)
			"
			@close="
				showLayerManager = false;
				showNDVI = false;
			"
		>
		</LayerManager>

		<!-- Traffic Density Panel -->
		<TrafficDensityPanel
			:visible="showTrafficDensity"
			:selectedCity="selectedCityName"
			:bbox="selectedCityBbox"
			:map="map"
			@close="showTrafficDensity = false"
			@results="onTrafficDensityResults"
		/>
	</div>
</template>

<script setup lang="ts">
import {
	ref,
	shallowRef,
	onMounted,
	onBeforeUnmount,
	watch,
	computed,
	nextTick,
} from "vue";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";
import CalendarWidget from "../ui/MaterialCalendar.vue";
import OpenWeatherLayer from "./OpenWeatherLayer.vue"; // OWM Integration
import WindyLayer from "./WindyLayer.vue"; // Windy Integration
import TrafficLayer from "./TrafficLayer.vue"; // TomTom Traffic
// import { LockIcon } from 'lucide-vue-next';
import { useToast } from "vue-toast-notification"; // [NEW]
import { useRoute, useRouter } from "vue-router"; // [NEW] Deep Linking
import { useMapContext } from "@/composables/useMapContext"; // [NEW] Map Context for AI

// Fix icons
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import iconUrl from "leaflet/dist/images/marker-icon.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
	iconRetinaUrl,
	iconUrl,
	shadowUrl,
});

import LayerManager from "./LayerManager.vue";
import MapSearch from "./MapSearch.vue";
import TimelapseControls from "./TimelapseControls.vue";
import DeliveryUpload from "./DeliveryUpload.vue"; // [NEW]
import SunTracker from "./SunTracker.vue"; // [NEW]
import ChargeMap from "./ChargeMap.vue"; // [NEW]
import TrafficDensityPanel from "./TrafficDensityPanel.vue"; // [NEW]

import axios from "@/axiosInstance";

// Constants
const TIMELAPSE_INTERVAL = 2000;
const DELIVERY_DEVICE_ID = 58; // Moak-58
const DELIVERY_THRESHOLD_METERS = 50;

// Tracker Helpers
const playbackIconSvg = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L2 22L12 18L22 22L12 2Z" fill="#2196F3" stroke="#FFFFFF" stroke-width="2" stroke-linejoin="round"/>
   </svg>`;

function toRad(deg: number) {
	return (deg * Math.PI) / 180;
}

function toDeg(rad: number) {
	return (rad * 180) / Math.PI;
}

function calculateBearing(
	lat1: number,
	lon1: number,
	lat2: number,
	lon2: number,
) {
	const dLon = toRad(lon2 - lon1);
	const y = Math.sin(dLon) * Math.cos(toRad(lat2));
	const x =
		Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
		Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLon);
	const brng = toDeg(Math.atan2(y, x));
	return (brng + 360) % 360;
}

function createRotatedIcon(angle: number) {
	return L.divIcon({
		className: "playback-marker-icon",
		html: `<div style="transform: rotate(${angle}deg); width: 24px; height: 24px;">${playbackIconSvg}</div>`,
		iconSize: [24, 24],
		iconAnchor: [12, 12],
		popupAnchor: [0, -12],
	});
}

function getDistance(lat1: number, lon1: number, lat2: number, lon2: number) {
	const R = 6371e3; // metres
	const φ1 = toRad(lat1);
	const φ2 = toRad(lat2);
	const Δφ = toRad(lat2 - lat1);
	const Δλ = toRad(lon2 - lon1);

	const a =
		Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
		Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

	return R * c;
}

const toast = useToast();
function showToast(
	msg: string,
	type: "success" | "info" | "warning" | "error" = "info",
) {
	toast.open({ message: msg, type: type });
}

const props = defineProps<{
	devices: any[];
	activeTrack?: {
		lat: number;
		lng: number;
		timestamp?: number | string;
		speed?: number;
		rpm?: number;
		fuel?: number;
	}[];
	activeTrackId?: string | number | null; // [NEW] Needed for calendar fetch
	loading?: boolean;
}>();

const emit = defineEmits(["close-track", "date-change"]);

const mapContainer = ref<HTMLElement | null>(null);
const map = shallowRef<any>(null); // [FIX] Use shallowRef to avoid deep proxying Leaflet instance
const markersLayer = shallowRef<any>(null);
const trackLayer = shallowRef<any>(null);
const playbackMarkerLayer = shallowRef<any>(null);

// Removed single ndviLayer ref in favor of wmsLayersMap map

// UI State
const showCalendar = ref(false);
const showNDVI = ref(false); // Toggle state (now controls LayerManager visibility)
const showWindy = ref(false); // [NEW] Windy Toggle
const windyLayer = ref("wind"); // Default to Windy forecast layer
const showSunTracker = ref(false); // [NEW] Sun Tracker Toggle
const sunTrackerRef = ref<any>(null); // [NEW] Ref for method call
const windyLayerRef = ref<any>(null); // Ref for WindyLayer exposed methods
const showTraffic = ref(false); // [NEW] Traffic Toggle
const lastSelectedLocation = ref<L.LatLng | null>(null); // [NEW] Memory for last click/search
const showChargeMap = ref(false); // [NEW] Charge Map Toggle
const chargeMapRef = ref<any>(null); // [NEW] Ref for ChargeMap exposed methods
const { updateContext, clearContext } = useMapContext(); // [NEW] Map Context for AI

// Traffic Density State
const showTrafficDensity = ref(false);
const selectedCityName = ref<string>();
const selectedCityBbox = ref<[number, number, number, number]>();
const trafficDensityLayer = shallowRef<any>(null);

// Copertura Radio State (OpenCellID) — sc-19255
const showCoverage = ref(false);
const coverageLayer = shallowRef<any>(null);
const coverageLoading = ref(false);
const coverageRadio = ref("LTE");
const coverageMode = ref<"live" | "static">("static");
const COVERAGE_LIVE_ZOOM = 8; // >= zoom → calcolo live area visibile; < → panoramica statica Italia
let coverageStaticCache: any = null;
let coverageReqId = 0;

// Rete Ferroviaria State (OSM) — sc-19262
const showRailway = ref(false);
const railwayLayer = shallowRef<any>(null);
let railwayCache: any = null;

// Watch Sun Tracker Toggle
watch(showSunTracker, (newVal) => {
	// If enabled and we have a memory, apply it immediately
	if (newVal && lastSelectedLocation.value && sunTrackerRef.value) {
		sunTrackerRef.value.setLocation(lastSelectedLocation.value);
	} else if (!newVal) {
		// Clear memory when Sun Tracker is closed to show hint next time
		lastSelectedLocation.value = null;
	}
});

// Computed: Determine which provider to use
const useWindyProvider = computed(() => {
	// Windy handles: wind, temp, pressure (forecast layers)
	return ["wind", "temp", "pressure"].includes(windyLayer.value);
});

function handleWindyMessage(event: MessageEvent) {
	// Reverted: No longer closing the weather service when the native Windy panel is closed.
}

function toggleWindy() {
	showWindy.value = !showWindy.value;
	if (showWindy.value) {
		windyLayer.value = "wind"; // reset: il pannello nativo Windy gestisce wind/temp/pressure
	}
	showToast(showWindy.value ? "Meteo Attivo" : "Meteo Disattivato", "info");
}

const showTimelapse = ref(false);
const calendarContainer = ref<HTMLElement | null>(null);

// Click Outside Logic
function handleClickOutside(event: MouseEvent) {
	if (showCalendar.value && calendarContainer.value) {
		// Check if click is outside calendar container AND not on the calendar button (to prevent immediate reopen)
		const target = event.target as HTMLElement;
		const btn = target.closest(".calendar-btn");
		if (!calendarContainer.value.contains(target) && !btn) {
			showCalendar.value = false;
		}
	}
}

let initialized = false;
onMounted(() => {
	document.addEventListener("click", handleClickOutside);
	window.addEventListener("message", handleWindyMessage);
	// Safety check: if map already exists, remove it first
	if (map.value) {
		map.value.remove();
		map.value = null;
	}
	if (initialized) return;
	initialized = true;
	initMap();
});

// onBeforeUnmount(() => {
//     document.removeEventListener('click', handleClickOutside);
//     if (map.value) {
//         map.value.off('pm:create'); // Cleanup drawing listener
//         map.value.remove();
//         map.value = null;
//     }
//     pausePlayback();
//     clearContext(); // Clear map context on unmount
// });

// [NEW] Spatial Analysis for drawn shapes

onBeforeUnmount(() => {
	document.removeEventListener("click", handleClickOutside);
	window.removeEventListener("message", handleWindyMessage);
	if (map.value) {
		// Rimuovi TUTTI gli event listener
		map.value.off(); // Rimuove tutti gli event listener

		// Rimuovi i layer esplicitamente
		if (markersLayer.value) {
			map.value.removeLayer(markersLayer.value);
			markersLayer.value = null;
		}
		if (trackLayer.value) {
			map.value.removeLayer(trackLayer.value);
			trackLayer.value = null;
		}
		if (playbackMarkerLayer.value) {
			map.value.removeLayer(playbackMarkerLayer.value);
			playbackMarkerLayer.value = null;
		}
		if (deliveryMarkersLayer.value) {
			map.value.removeLayer(deliveryMarkersLayer.value);
			deliveryMarkersLayer.value = null;
		}

		// Rimuovi la mappa
		map.value.remove();
		map.value = null;
	}
	pausePlayback();
	clearContext(); // Clear map context on unmount
});

function analyzeDrawnArea(layer: any) {
	if (!map.value) return;

	const bounds = layer.getBounds();
	const center = bounds.getCenter();

	// Calculate area in km²
	const ne = bounds.getNorthEast();
	const sw = bounds.getSouthWest();
	const latDiff = Math.abs(ne.lat - sw.lat) * 111; // ~111 km per degree lat
	const lngDiff =
		Math.abs(ne.lng - sw.lng) * 111 * Math.cos((center.lat * Math.PI) / 180);
	const areaKm2 = latDiff * lngDiff;

	// Get charging stations in area
	let stationsInArea: any[] = [];
	const byPower: Record<string, number> = {};
	const byStatus: Record<string, number> = {};

	if (chargeMapRef.value?.getAllStations) {
		const allStations = chargeMapRef.value.getAllStations();
		stationsInArea = allStations.filter((station: any) => {
			const lat = station.AddressInfo?.Latitude;
			const lng = station.AddressInfo?.Longitude;
			if (!lat || !lng) return false;
			return bounds.contains([lat, lng]);
		});

		// Aggregate by power and status
		stationsInArea.forEach((station: any) => {
			// Status
			const status = station.StatusType?.Title || "Unknown";
			byStatus[status] = (byStatus[status] || 0) + 1;

			// Power (from first connection)
			if (station.Connections?.[0]?.PowerKW) {
				const power = `${station.Connections[0].PowerKW} kW`;
				byPower[power] = (byPower[power] || 0) + 1;
			}
		});
	}

	// Update map context for Gemini AI
	updateContext({
		area: {
			type: layer.pm?._shape === "Circle" ? "circle" : "rectangle",
			bounds: [
				[sw.lat, sw.lng],
				[ne.lat, ne.lng],
			],
			centerCoordinates: [center.lat, center.lng],
			areaKm2,
		},
		chargingStations: {
			total: stationsInArea.length,
			byPower,
			byStatus,
			stations: stationsInArea.slice(0, 20).map((s: any) => ({
				id: s.ID,
				name: s.AddressInfo?.Title || "Unknown",
				lat: s.AddressInfo?.Latitude,
				lng: s.AddressInfo?.Longitude,
				power: s.Connections?.[0]?.PowerKW
					? `${s.Connections[0].PowerKW} kW`
					: "N/A",
				status: s.StatusType?.Title || "Unknown",
			})),
		},
		traffic: showTraffic.value ? { level: "unknown" } : undefined,
		weather: showWindy.value
			? {
					current: {
						temp: 0,
						precipitation: 0,
						description: "Dati meteo attivi",
					},
					forecast: [],
			  }
			: undefined,
		timestamp: Date.now(),
	});

	showToast(
		`📍 Area analizzata: ${stationsInArea.length} colonnine trovate`,
		"success",
	);
}

// Setup Geoman drawing event listener
watch(map, (newMap) => {
	if (newMap) {
		newMap.on("pm:create", (e: any) => {
			analyzeDrawnArea(e.layer);
		});
	}
});

// Lock State
const lockedDeviceId = ref<number | string | null>(null);
const isFollowing = ref(true); // [NEW] Default to auto-follow
// Layer Management
interface WMSLayer {
	Name: string;
	Title: string;
	Abstract?: string;
}

interface ActiveLayer {
	id: string;
	title: string;
	opacity: number;
	date?: string; // Specific date for this layer
}

const showLayerManager = ref(false);
const loadingLayers = ref(false);
const availableLayers = ref<WMSLayer[]>([]);
const activeLayers = ref<ActiveLayer[]>([]);
// Map of layerId -> Leaflet Layer instance
const wmsLayersMap = new Map<string, any>();
const currentZoom = ref(10); // Default to safe zoom
const wmsGlobalDate = ref<string | undefined>(undefined); // [NEW] Global date state
const availableDatesMap = ref<Record<string, string[]>>({}); // Map layerId -> dates

// Helper to get date for a layer (defaults to activeTrack or Today)
const route = useRoute();
const router = useRouter();

// URL SYNC
// Restore from URL is handled in PARENT (LiveMaps.vue) to ensure correct data fetching.

// Force lock if prop changes (e.g. from parent deep link restore)
watch(
	() => props.activeTrackId,
	(newVal) => {
		if (newVal) {
			lockedDeviceId.value = newVal;
			isFollowing.value = true; // Auto-follow on new track
		}
	},
	{ immediate: true },
);

// Sync State TO Url
watch([lockedDeviceId, () => props.activeTrack], () => {
	// If we are initializing or loading, do not mess with URL params excessively
	// Also if activeTrack is empty but we have a lockedDeviceId, wait.
	if (props.loading) return;

	const query: any = { ...route.query };

	// update device
	if (lockedDeviceId.value) {
		query.device = lockedDeviceId.value;
	} else {
		delete query.device;
	}

	// update date
	if (props.activeTrack && props.activeTrack.length > 0) {
		// get date from first point
		const first = props.activeTrack[0];
		if (first.timestamp) {
			const ts =
				Number(first.timestamp) *
				(String(first.timestamp).length === 10 ? 1000 : 1);
			const d = new Date(ts);
			const year = d.getFullYear();
			const month = String(d.getMonth() + 1).padStart(2, "0");
			const day = String(d.getDate()).padStart(2, "0");
			query.date = `${year}-${month}-${day}`;
		}
	} else {
		// Only delete date if we are strictly NOT loading and have no track.
		// And maybe explicitly check if we are in a state where date SHOULD be cleared?
		// E.g. Realtime mode.
		// But if user selected a date and it has 0 points (empty track),
		// removing the date param makes the user lose context.
		// So, if we have a locked device, we should probably KEEP the date param
		// if it matches what we requested?
		// For now, let's just NOT delete it if it's already there and we are in a "no track" state.
		// But if user switches to Realtime? Realtime usually means activeTrack is empty?
		// Actually, in Live Mode, activeTrack is empty?
		// No, Live Mode usually has 1 point? Or array of recent points?
		// Let's safe check: if route.query.date matches the requested date, keep it?
		// BETTER FIX: Do not delete date if activeTrack is empty.
		// Only update it if activeTrack HAS a date.
		// If user manually clears date, that's different.
		// But how to detect manual clear vs empty track?
		// Let's simply comment out the delete for now to prevent the bug.
		// The URL date might become stale if you switch to realtime, but that's better than loop.
		// delete query.date;
	}

	// Replace URL
	router.replace({ query }).catch(() => {});
});

function copyShareLink() {
	const url = window.location.href;
	navigator.clipboard
		.writeText(url)
		.then(() => {
			showToast("Link copiato!", "success");
		})
		.catch(() => {
			showToast("Errore copia link", "error");
		});
}

// [FIX] Update all active layers with the new date
function onLayerDateChange(val?: string) {
	wmsGlobalDate.value = val; // [FIX] Update local state to reflect in UI

	if (!activeLayers.value.length) return;

	// Update all active layers with the selected date
	activeLayers.value.forEach((layer) => {
		layer.date = val;
	});

	updateActiveLayers();
}

function zoomIn() {
	if (map.value) map.value.zoomIn();
}

function zoomToSatellite() {
	if (map.value) {
		map.value.setZoom(10, { animate: true });
	}
}

function zoomOut() {
	if (map.value) map.value.zoomOut();
}

function onMapSearchSelect(loc: {
	lat: number;
	lng: number;
	label: string;
	bbox?: [number, number, number, number];
}) {
	if (!map.value) return;

	// Store as last location
	lastSelectedLocation.value = L.latLng(loc.lat, loc.lng);

	// Store city info for Traffic Density
	if (loc.bbox) {
		selectedCityName.value = loc.label.split(",")[0]; // First part is usually city name
		selectedCityBbox.value = loc.bbox;
	}

	// Fly to location
	map.value.flyTo([loc.lat, loc.lng], 16, {
		duration: 1.5,
	});

	// Optional: Add temporary marker?
	// For now just moving the view is usually enough, but a brief popup helps confirm.
	L.popup()
		.setLatLng([loc.lat, loc.lng])
		.setContent(`<b>${loc.label}</b>`)
		.openOn(map.value);

	// Se Windy è attivo, sincronizza anche la mappa interna dell'iframe
	if (showWindy.value && useWindyProvider.value && windyLayerRef.value) {
		windyLayerRef.value.flyTo(loc.lat, loc.lng, 10);
	}

	// [NEW] If Sun Tracker is active, set its location
	if (showSunTracker.value && sunTrackerRef.value) {
		sunTrackerRef.value.setLocation(L.latLng(loc.lat, loc.lng));
	}
}

// Traffic Density Results Handler
function onTrafficDensityResults(geojson: any) {
	if (!map.value) return;

	// Remove existing layer if present
	if (trafficDensityLayer.value) {
		map.value.removeLayer(trafficDensityLayer.value);
		trafficDensityLayer.value = null;
	}

	console.log("[LiveMap] Rendering traffic density data:", geojson);

	// Color scale based on sample size (traffic volume)
	function getColor(sampleSize: number): string {
		if (sampleSize > 1000) return "#e74c3c"; // Very high - red
		if (sampleSize > 500) return "#e67e22"; // High - orange
		if (sampleSize > 100) return "#f1c40f"; // Medium - yellow
		return "#2ecc71"; // Low - green
	}

	// Style function for GeoJSON features
	function style(feature: any) {
		const sampleSize = feature.properties?.sampleSize || 0;
		return {
			color: getColor(sampleSize),
			weight: 4,
			opacity: 0.8,
		};
	}

	// Create GeoJSON layer
	trafficDensityLayer.value = L.geoJSON(geojson, {
		style: style,
		onEachFeature: (feature, layer) => {
			// Add popup with details
			const props = feature.properties || {};

			// Handle both string (demo mode) and number (real API) types
			const length =
				typeof props.length === "string"
					? props.length
					: (props.length || 0).toFixed(2);
			const avgSpeed =
				typeof props.averageSpeed === "string"
					? props.averageSpeed
					: (props.averageSpeed || 0).toFixed(1);

			const popup = `
                <div style="font-family: Inter, sans-serif;">
                    <b>${props.roadName || "Strada"}</b><br>
                    <b>Densità:</b> ${props.sampleSize || 0} veicoli<br>
                    <b>Lunghezza:</b> ${length} km<br>
                    <b>Velocità Media:</b> ${avgSpeed} km/h<br>
                    <b>FRC:</b> ${props.frc || "N/A"}<br>
                    ${
											props.timeSet ? `<b>Fascia:</b> ${props.timeSet}<br>` : ""
										}
                </div>
            `;
			layer.bindPopup(popup);
		},
	}).addTo(map.value);

	// Fit map to layer bounds
	const bounds = trafficDensityLayer.value.getBounds();
	if (bounds.isValid()) {
		map.value.fitBounds(bounds, { padding: [50, 50] });
	}

	showToast("Dati densità traffico caricati!", "success");
}

// Clear Traffic Density Layer
function clearTrafficDensityLayer() {
	if (!map.value || !trafficDensityLayer.value) return;

	map.value.removeLayer(trafficDensityLayer.value);
	trafficDensityLayer.value = null;

	showToast("Layer densità traffico rimosso", "info");
}

// ===== Copertura Radio (OpenCellID) — sc-19255 =====
function toggleCoverage() {
	showCoverage.value = !showCoverage.value;
	if (showCoverage.value) {
		refreshCoverage();
	} else {
		clearCoverageLayer();
	}
}

// Decide static (zoom-out, panoramica Italia) vs live (zoom-in, area visibile) e carica.
async function refreshCoverage() {
	if (!map.value || !showCoverage.value) return;
	const zoom = map.value.getZoom();
	const mode: "live" | "static" = zoom >= COVERAGE_LIVE_ZOOM ? "live" : "static";
	coverageMode.value = mode;
	const reqId = ++coverageReqId;
	coverageLoading.value = true;
	const t0 = performance.now();
	try {
		let geojson: any;
		if (mode === "static") {
			if (!coverageStaticCache) {
				const { data } = await axios.get("/coverage/static", { withCredentials: true });
				coverageStaticCache = data;
			}
			geojson = coverageStaticCache;
		} else {
			const b = map.value.getBounds();
			const bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]
				.map((n: number) => n.toFixed(4))
				.join(",");
			// cella adattiva allo zoom: più grossa allo zoom-out (meno celle, resa coerente)
			const cell = zoom >= 13 ? 0.008 : zoom >= 11 ? 0.015 : zoom >= 9 ? 0.025 : 0.04;
			const { data } = await axios.get("/coverage", {
				params: { bbox, radio: coverageRadio.value, cell },
				withCredentials: true,
			});
			geojson = data;
		}
		if (reqId !== coverageReqId || !showCoverage.value) return; // richiesta superata
		renderCoverage(geojson);
		console.log(
			`[LiveMap] copertura ${mode}: ${geojson.features?.length || 0} celle in ${(performance.now() - t0).toFixed(0)}ms`
		);
	} catch (e: any) {
		// 400 = area troppo grande per il calcolo live: silenzioso, si zooma per vederla
		if (e?.response?.status === 400) {
			console.warn("[LiveMap] copertura: area troppo grande per il live, zoomare");
		} else {
			console.error("[LiveMap] errore caricamento copertura", e);
			showToast("Errore caricamento copertura radio", "error");
		}
	} finally {
		if (reqId === coverageReqId) coverageLoading.value = false;
	}
}

const COVERAGE_EXPAND = 1.4;
const COVERAGE_BLUR_PX = 8;
const COVERAGE_PANE_OPACITY = 0.55;

function coverageStyle(feature: any) {
	const dead = feature.properties?.level === "dead";
	return {
		stroke: false,
		fillColor: dead ? "#e74c3c" : "#f39c12",
		fillOpacity: 1,
	};
}

function expandCellFeature(feature: any): any {
	const ring = feature?.geometry?.coordinates?.[0];
	if (!ring || !ring.length) return feature;
	let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
	for (const c of ring) {
		const lng = c[0], lat = c[1];
		if (lng < minLng) minLng = lng;
		if (lng > maxLng) maxLng = lng;
		if (lat < minLat) minLat = lat;
		if (lat > maxLat) maxLat = lat;
	}
	const cx = (minLng + maxLng) / 2, cy = (minLat + maxLat) / 2;
	const hw = ((maxLng - minLng) / 2) * COVERAGE_EXPAND;
	const hh = ((maxLat - minLat) / 2) * COVERAGE_EXPAND;
	const out = [
		[cx - hw, cy - hh],
		[cx + hw, cy - hh],
		[cx + hw, cy + hh],
		[cx - hw, cy + hh],
		[cx - hw, cy - hh],
	];
	return {
		type: "Feature",
		properties: feature.properties,
		geometry: { type: "Polygon", coordinates: [out] },
	};
}

function renderCoverage(geojson: any) {
	if (!map.value) return;
	if (!map.value.getPane("coverage")) {
		map.value.createPane("coverage");
		const pane = map.value.getPane("coverage");
		if (pane) {
			pane.style.zIndex = "410";
			pane.style.opacity = String(COVERAGE_PANE_OPACITY);
			pane.style.filter = `blur(${COVERAGE_BLUR_PX}px)`;
			pane.style.pointerEvents = "none";
		}
	}
	if (coverageLayer.value) {
		map.value.removeLayer(coverageLayer.value);
		coverageLayer.value = null;
	}
	const feats = geojson?.features || [];
	const weak = feats.filter((f: any) => f?.properties?.level !== "dead").map(expandCellFeature);
	const dead = feats.filter((f: any) => f?.properties?.level === "dead").map(expandCellFeature);
	const ordered = { type: "FeatureCollection", features: [...weak, ...dead] };
	coverageLayer.value = L.geoJSON(ordered, {
		style: coverageStyle,
		interactive: false,
		pane: "coverage",
		renderer: L.canvas({ pane: "coverage", padding: 0.5 }),
	}).addTo(map.value);
}

function clearCoverageLayer() {
	if (map.value && coverageLayer.value) {
		map.value.removeLayer(coverageLayer.value);
		coverageLayer.value = null;
	}
	coverageLoading.value = false;
}

// ===== Rete Ferroviaria (OSM) — sc-19262 =====
async function toggleRailway() {
	showRailway.value = !showRailway.value;
	if (!showRailway.value) {
		if (map.value && railwayLayer.value) {
			map.value.removeLayer(railwayLayer.value);
			railwayLayer.value = null;
		}
		return;
	}
	try {
		if (!railwayCache) {
			const { data } = await axios.get("/railway/static", { withCredentials: true });
			railwayCache = data;
		}
		if (!showRailway.value || !map.value) return;
		if (railwayLayer.value) map.value.removeLayer(railwayLayer.value);
		// Doppia passata per "casing" (bordo bianco + linea scura) → risalta sulla mappa
		railwayLayer.value = L.geoJSON(railwayCache, {
			interactive: false,
			renderer: L.canvas({ padding: 0.5 }),
			style: { color: "#7b1fa2", weight: 2.2, opacity: 0.95 },
		}).addTo(map.value);
		console.log(`[LiveMap] rete ferroviaria: ${railwayCache.features?.length || 0} tratte`);
	} catch (e) {
		console.error("[LiveMap] errore caricamento rete ferroviaria", e);
		showToast("Errore caricamento rete ferroviaria", "error");
		showRailway.value = false;
	}
}

async function fetchAvailableDates(
	year: number,
	month: number,
	layerId: string,
) {
	if (!map.value || !layerId) return;

	// Use Copernicus OData Catalogue (Public)
	// This allows us to find satellite passes independently of the specific WMS instance configuration.
	// Base URL: https://catalogue.dataspace.copernicus.eu/odata/v1/Products

	// 1. Calculate Time Range
	const startObj = new Date(year, month, 1);
	const endObj = new Date(year, month + 1, 1); // First day of next month to cover full last day
	const startStr = startObj.toISOString();
	const endStr = endObj.toISOString();

	// 2. Calculate Polygon (WKT) from Map Bounds
	// SRID=4326;POLYGON((lon1 lat1, lon2 lat1, lon2 lat2, lon1 lat2, lon1 lat1))
	const b = map.value.getBounds();
	const minLat = b.getSouth();
	const minLon = b.getWest();
	const maxLat = b.getNorth();
	const maxLon = b.getEast();

	// Ensure coordinates are within valid ranges if needed, but usually Leaflet handles basic wrapping.
	// Construct Polygon (Counter-Clockwise)
	const polygon = `SRID=4326;POLYGON((${minLon} ${minLat}, ${maxLon} ${minLat}, ${maxLon} ${maxLat}, ${minLon} ${maxLat}, ${minLon} ${minLat}))`;

	// 3. Construct OData Filter
	// Collection: SENTINEL-2
	// Intersects map area
	// Time range

	// Note: We intentionally don't filter by cloud cover here to show ALL passes,
	// but we could add "Attributes/OData.CSC.DoubleAttribute/any(att:att/Name eq 'cloudCover' and att/Value lt 100.0)"

	const filter = `Collection/Name eq 'SENTINEL-2' and OData.CSC.Intersects(area=geography'${polygon}') and ContentDate/Start ge ${startStr} and ContentDate/Start lt ${endStr}`;

	const url = `https://catalogue.dataspace.copernicus.eu/odata/v1/Products?$filter=${encodeURIComponent(
		filter,
	)}&$select=ContentDate&$top=100`;

	try {
		const res = await fetch(url);
		if (!res.ok) throw new Error("OData Error " + res.status);
		const json = await res.json();

		if (json.value) {
			// OData returns { value: [...] }
			const dates = new Set<string>();
			json.value.forEach((item: any) => {
				// item.ContentDate.Start
				if (item.ContentDate && item.ContentDate.Start) {
					dates.add(item.ContentDate.Start.split("T")[0]);
				}
			});

			// Update map for specific layer
			availableDatesMap.value = {
				...availableDatesMap.value,
				[layerId]: Array.from(dates),
			};
			console.log(
				`[OData] Found ${dates.size} dates for ${layerId} using Public Catalog`,
			);
		}
	} catch (e) {
		console.warn(`[OData] Could not fetch dates for ${layerId}:`, e);
	}
}

// computed property to check if we should show the warning
const showZoomWarning = computed(() => {
	return activeLayers.value.length > 0 && currentZoom.value < 10;
});

// Playback State
const isPlaying = ref(false);
const playbackIndex = ref(0);
const playbackSpeed = ref(1);
const speedOptions = [0.2, 0.5, 1, 2, 4, 8, 16];
let animationFrameId: number | null = null;
let lastFrameTime = 0;

// Custom Icons
const arrowIconSvg = `
<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M12 2L2 22L12 18L22 22L12 2Z" fill="{COLOR}" stroke="#ffffff" stroke-width="2" stroke-linejoin="round"/>
</svg>`;

function createArrowIcon(color: string, angle: number) {
	return L.divIcon({
		className: "arrow-icon",
		html: `<div style="transform: rotate(${angle}deg); width: 24px; height: 24px;">
                ${arrowIconSvg.replace("{COLOR}", color)}
               </div>`,
		iconSize: [24, 24],
		iconAnchor: [12, 12],
		popupAnchor: [0, -10],
	});
}

function updateMarkers() {
	if (!map.value || !markersLayer.value || !map.value._container) return;

	markersLayer.value.clearLayers();

	props.devices.forEach((device) => {
		const latLng = getDeviceLatLng(device);

		if (latLng && map.value && markersLayer.value) {
			const isMoving = parseFloat(device.latest_data?.vehicle_speed || "0") > 0;
			const color = isMoving ? "#00b200" : "#f44336"; // Green if moving, Red if stopped

			// Determine rotation: use heading if available, otherwise 0
			let angle =
				device.latest_data?.heading || device.latest_data?.course || 0;

			const icon = createArrowIcon(color, angle);

			const marker = L.marker(latLng, { icon }).bindPopup(`
                    <div style="text-align:center;">
                        <b>${device.name || device.vin}</b><br>
                        <span style="color:${color}; font-weight:bold;">
                            ${isMoving ? "In Movimento" : "Fermo"}
                        </span><br>
                        ${
													isMoving
														? `Speed: ${device.latest_data?.vehicle_speed} km/h`
														: ""
												}
                    </div>
                `);

			markersLayer.value!.addLayer(marker);
		}
	});
}
watch(
	() => props.activeTrack,
	() => {
		pausePlayback();
		playbackIndex.value = 0;
		updateTrack();
		updatePlaybackMarker();
	},
	{ deep: true },
);

/* ---------------------------------------------------- */
/*  DATA HELPERS                                        */
/* ---------------------------------------------------- */

const getDeviceLatLng = (d: any): [number, number] | null => {
	if (!d) return null;

	// 1. Static Position override (High priority for static)
	if (d.is_static) {
		if (Array.isArray(d.location) && d.location.length === 2) {
			return [d.location[0], d.location[1]];
		}
		if (d.location?.lat && d.location?.lng) {
			return [d.location.lat, d.location.lng];
		}
	}

	// 2. nested latest_data (Real GPS - Primary for dynamic)
	if (d.latest_data?.latitude && d.latest_data?.longitude) {
		return [Number(d.latest_data.latitude), Number(d.latest_data.longitude)];
	}

	// 3. Location fallback for dynamic devices (if latest_data missing)
	if (Array.isArray(d.location) && d.location.length === 2) {
		return [d.location[0], d.location[1]];
	}
	if (d.location?.lat && d.location?.lng) {
		return [d.location.lat, d.location.lng];
	}

	// 4. Top level fallback
	if (d.latitude && d.longitude) {
		return [Number(d.latitude), Number(d.longitude)];
	}

	// 5. LiveMaps might pass { lat, lng } if it's a track point disguised as device
	if (d.lat && d.lng) {
		return [Number(d.lat), Number(d.lng)];
	}

	return null;
};

function formatTimestamp(ts: any) {
	if (!ts) return "--:--";
	const d = new Date(Number(ts) * (String(ts).length === 10 ? 1000 : 1));
	return d.toLocaleString("it-IT", {
		day: "2-digit",
		month: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
	});
}

const currentDateStr = computed(() => {
	if (props.activeTrack && props.activeTrack.length > 0) {
		const ts = props.activeTrack[0].timestamp;
		// Defensive check
		if (ts) {
			const d = new Date(Number(ts) * (String(ts).length === 10 ? 1000 : 1));
			// Use local time, not UTC (toISOString uses UTC)
			const year = d.getFullYear();
			const month = String(d.getMonth() + 1).padStart(2, "0");
			const day = String(d.getDate()).padStart(2, "0");
			return `${year}-${month}-${day}`;
		}
	}
	const now = new Date();
	const year = now.getFullYear();
	const month = String(now.getMonth() + 1).padStart(2, "0");
	const day = String(now.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
});

/* ---------------------------------------------------- */
/*  MAP UPDATES                                         */
/* ---------------------------------------------------- */

// onMounted(() => {
//     initMap();
// });

/* ---------------------------------------------------- */
/*  WATCHERS                                            */
/* ---------------------------------------------------- */
watch(
	() => props.devices,
	(newDevices) => {
		updateMarkers();

		// Follow functionality is handled by unified watcher now

		// Check Delivery (Device 58) logic

		// Check Delivery (Device 58) logic
		if (deliveryPoints.value.length > 0) {
			const truck = newDevices.find(
				(d) => d.id == DELIVERY_DEVICE_ID || d.name?.includes("58"),
			);
			if (truck) {
				const truckPos = getDeviceLatLng(truck);
				if (truckPos) checkDeliveryProximity(truckPos);
			}
		}
	},
	{ deep: true },
);

onBeforeUnmount(() => {
	if (map.value) {
		map.value.remove();
		map.value = null;
	}
	pausePlayback();
});

function initMap() {
	console.log("[LiveMap] Initializing Map...");
	if (!mapContainer.value) {
		console.error("[LiveMap] Map Container not found!");
		return;
	}

	// Safety check: Avoid "Map container is already initialized"
	if (map.value) {
		console.warn("[LiveMap] Map already initialized, removing.");
		map.value.remove();
		map.value = null;
	}
	// Also check if the DOM element has leaflet class already (double safety)
	const container = mapContainer.value as any;
	if (container._leaflet_id) {
		// This is the lower level leaflet check
		container._leaflet_id = null;
	}

	map.value = L.map(mapContainer.value, {
		zoomControl: false,
	}).setView([41.9028, 12.4964], 6);

	// Disable follow mode on user interaction
	map.value.on("dragstart", () => {
		if (isFollowing.value) {
			isFollowing.value = false;
			toast.info("Auto-follow sospeso");
		}
	});
	map.value.on("zoomstart", () => {
		// Optionally disable on zoom too, or just drag?
		// User said "come faccio zoom... esce dal tracciato".
		// Maybe they want to zoom WHILE following?
		// Usually zooming centers on cursor. If we force center on device 100ms later, it feels glitchy.
		// Let's disable follow on zoom start too for safety.
		if (isFollowing.value) {
			isFollowing.value = false;
			// toast.info("Auto-follow sospeso"); // Zoom might be frequent, maybe skip toast
		}
	});

	// [NEW] Global Click Listener for Sun Tracker memory
	map.value.on("click", (e: L.LeafletMouseEvent) => {
		// Always store last click
		lastSelectedLocation.value = e.latlng;
	});

	console.log("[LiveMap] Map created, adding tiles...");

	L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
		attribution: "&copy; OpenStreetMap contributors",
	}).addTo(map.value);

	// Standard Zoom Control
	L.control.zoom({ position: "topright" }).addTo(map.value);

	// Initialize Geoman
	if (map.value.pm) {
		map.value.pm.addControls({
			position: "topleft",
			drawCircle: false,
			drawCircleMarker: false,
			drawPolyline: true,
			drawRectangle: true,
			drawPolygon: true,
			drawMarker: true,
			drawText: false,
			cutPolygon: false,
			rotateMode: false,
			editMode: true,
			dragMode: true,
			removalMode: true,
		});

		// [NEW] Auto-disengage Follow Mode on manual interaction
		// map.value.on('dragstart', () => {
		//     if (lockedDeviceId.value) {
		//         lockedDeviceId.value = null;
		//         showToast("Follow Disattivato", "info");
		//     }
		// });
		// Define languages for tooltips (Optional)
		map.value.pm.setLang("it");
	}

	// Initial zoom
	currentZoom.value = map.value.getZoom();

	map.value.on("zoomend", () => {
		currentZoom.value = map.value.getZoom();
	});

	// Copertura radio: ricarica su spostamento/zoom quando attiva (debounce)
	let coverageMoveTimer: any = null;
	map.value.on("moveend", () => {
		if (!showCoverage.value) return;
		clearTimeout(coverageMoveTimer);
		coverageMoveTimer = setTimeout(() => refreshCoverage(), 350);
	});

	markersLayer.value = L.layerGroup().addTo(map.value);
	playbackMarkerLayer.value = L.layerGroup().addTo(map.value);

	updateMarkers();
}

function toggleNDVI() {
	showLayerManager.value = !showLayerManager.value;
	showNDVI.value = showLayerManager.value; // Sync for button active state

	if (showLayerManager.value && availableLayers.value.length === 0) {
		fetchWMSCapabilities();
	}
}

async function fetchWMSCapabilities() {
	loadingLayers.value = true;
	try {
		const url = `/proxy/copernicus-wms?SERVICE=WMS&REQUEST=GetCapabilities`;
		console.log("[WMS] Fetching capabilities from:", url);
		const response = await fetch(url);
		const text = await response.text();

		const parser = new DOMParser();
		const xmlDoc = parser.parseFromString(text, "text/xml");

		// Flatten layers. usually there is a root layer and children.
		// We want leaf layers or all named layers.
		const layers = Array.from(xmlDoc.querySelectorAll("Layer > Name"))
			.map((node) => {
				const layerNode = node.parentElement;
				return {
					Name: node.textContent || "",
					Title: layerNode?.querySelector("Title")?.textContent || "",
					Abstract: layerNode?.querySelector("Abstract")?.textContent || "",
				};
			})
			.filter((l) => l.Name !== ""); // Filter out layers without name (usually folders)

		availableLayers.value = layers;
	} catch (e) {
		console.error("Error fetching WMS capabilities:", e);
	} finally {
		loadingLayers.value = false;
	}
}

function updateActiveLayers() {
	if (!map.value) return;

	const wmsUrl = `/proxy/copernicus-wms`;

	// 1. Remove layers that are no longer active
	const activeIds = new Set(activeLayers.value.map((l) => l.id));
	Array.from(wmsLayersMap.entries()).forEach(([id, layer]) => {
		if (!activeIds.has(id)) {
			map.value.removeLayer(layer);
			wmsLayersMap.delete(id);
		}
	});

	// 2. Add or Update layers
	activeLayers.value.forEach((activeLayer) => {
		let layer = wmsLayersMap.get(activeLayer.id);

		// Determine date: specific > default (undefined = Latest)
		const targetDate = activeLayer.date || wmsGlobalDate.value;
		const newTime = targetDate ? `${targetDate}/${targetDate}` : undefined;

		if (!layer) {
			// Create new layer
			const wmsParams: any = {
				layers: activeLayer.id,
				format: "image/png",
				transparent: true,
				version: "1.3.0",
				attribution: "&copy; Copernicus Data Space Ecosystem",
				opacity: activeLayer.opacity,
				minZoom: 10,
			};

			if (newTime) {
				wmsParams.TIME = newTime;
			}

			// Trigger availability fetch initially to populate calendar
			const now = new Date();
			fetchAvailableDates(now.getFullYear(), now.getMonth(), activeLayer.id);

			layer = L.tileLayer.wms(wmsUrl, wmsParams).addTo(map.value);

			wmsLayersMap.set(activeLayer.id, layer);
		} else {
			// Update Opacity
			if (layer.options.opacity !== activeLayer.opacity) {
				layer.setOpacity(activeLayer.opacity);
			}
			// Update Date
			if (newTime) {
				if (layer.wmsParams.TIME !== newTime) {
					console.log(
						`[LiveMap] Updating layer ${activeLayer.id} TIME to: ${newTime}`,
					);
					layer.setParams({ TIME: newTime });
				}
			} else {
				// If switching to latest, remove TIME param
				if (layer.wmsParams.TIME) {
					console.log(
						`[LiveMap] Removing TIME param for ${activeLayer.id} (Latest)`,
					);
					delete layer.wmsParams.TIME;
					layer.redraw();
				}
			}
		}
	});
}

watch(
	activeLayers,
	() => {
		updateActiveLayers();
	},
	{ deep: true },
);

// [FIX] Force redraw when entering satellite zoom range (10+)
watch(currentZoom, (newZoom, oldZoom) => {
	if (newZoom >= 10 && oldZoom < 10) {
		if (activeLayers.value.length > 0) {
			console.log(
				"[LiveMap] Zoom threshold crossed (>=10), forcing WMS redraw",
			);
			wmsLayersMap.forEach((layer) => {
				layer.redraw();
				// If redraw isn't enough, we might need setParams logic, but redraw should check bounds/zoom.
			});
		}
	}
});

// Watchers
watch(
	() => props.devices,
	() => {
		updateMarkers();
	},
	{ deep: true },
);

// ... (existing watch for activeTrack)

/* ---------------------------------------------------- */
/*  DATA HELPERS                                        */
/* ---------------------------------------------------- */

function updateTrack() {
	if (!map.value) return;

	if (trackLayer.value) {
		map.value.removeLayer(trackLayer.value);
		trackLayer.value = null;
	}

	if (props.activeTrack && props.activeTrack.length > 1) {
		// Use FeatureGroup to hold multiple segments
		const group = L.featureGroup().addTo(map.value);
		trackLayer.value = group;

		const getColor = (speed: number) => (speed >= 5 ? "#4caf50" : "#ffeb3b"); // Green vs Yellow

		let currentPoints: [number, number][] = [];
		let currentColor = "";

		// Initialize
		if (props.activeTrack.length > 0) {
			const first = props.activeTrack[0];
			currentColor = getColor(first.speed || 0);
			currentPoints.push([first.lat, first.lng]);
		}

		for (let i = 1; i < props.activeTrack.length; i++) {
			const p = props.activeTrack[i];
			const pSpeed = p.speed || 0;
			const color = getColor(pSpeed);

			if (color !== currentColor) {
				// Finish current segment (add current point to connect)
				currentPoints.push([p.lat, p.lng]);

				L.polyline(currentPoints, {
					color: currentColor,
					weight: 5,
					opacity: 0.8,
				}).addTo(group);

				// Start new segment from this point
				currentPoints = [[p.lat, p.lng]];
				currentColor = color;
			} else {
				currentPoints.push([p.lat, p.lng]);
			}
		}

		// Add last segment
		if (currentPoints.length > 1) {
			L.polyline(currentPoints, {
				color: currentColor,
				weight: 5,
				opacity: 0.8,
			}).addTo(group);
		}

		// Fit bounds
		const bounds = group.getBounds();
		if (bounds.isValid()) {
			map.value.fitBounds(bounds, { padding: [50, 50] });
		}
	}
}

/* ---------------------------------------------------- */
/*  COMPUTED TIMELINE LOGIC                             */
/* ---------------------------------------------------- */

// 1. Segments (Moving, Idle, Parking)
const timelineSegments = computed(() => {
	if (!props.activeTrack || props.activeTrack.length === 0) return [];

	const segments = [];
	let currentType = "";
	let count = 0;

	for (const loc of props.activeTrack) {
		const speed = loc.speed || 0;
		const rpm = loc.rpm || 0;
		let type = "parking"; // blue/gray

		if (speed >= 5) {
			type = "moving"; // green
		} else if (rpm > 0) {
			type = "idle"; // yellow
		}

		if (type !== currentType) {
			if (count > 0) {
				segments.push({ type: currentType, count });
			}
			currentType = type;
			count = 1;
		} else {
			count++;
		}
	}
	if (count > 0) segments.push({ type: currentType, count });

	const total = props.activeTrack.length;
	return segments.map((s) => ({
		type: s.type,
		width: (s.count / total) * 100,
	}));
});

// 2. Gaps (> 30 min)
const timelineGaps = computed(() => {
	if (!props.activeTrack || props.activeTrack.length < 2) return [];
	const gaps = [];
	const GAP_THRESHOLD = 30 * 60;

	for (let i = 0; i < props.activeTrack.length - 1; i++) {
		const curr = props.activeTrack[i];
		const next = props.activeTrack[i + 1];

		if (curr.timestamp && next.timestamp) {
			const t1 =
				Number(curr.timestamp) *
				(String(curr.timestamp).length === 10 ? 1 : 0.001); // normalize to sec? Usually timestamp is sec or ms.
			// RouteMap uses: typeof curr.timestamp === 'string' ? parseInt(curr.timestamp) : curr.timestamp; assuming sec?
			// Let's safe parse.
			const ts1 = Number(curr.timestamp);
			const ts2 = Number(next.timestamp);
			// Assume both are seconds or both ms.
			const diff = ts2 - ts1;
			// Determine if sec or ms based on magnitude? > 10000000000 is ms
			const isMs = ts1 > 10000000000;
			const diffSec = isMs ? diff / 1000 : diff;

			if (diffSec > GAP_THRESHOLD) {
				const hours = Math.floor(diffSec / 3600);
				const mins = Math.floor((diffSec % 3600) / 60);
				let label = hours > 0 ? `${hours}h ` : "";
				label += `${mins}m`;

				gaps.push({
					left: ((i + 0.5) / props.activeTrack.length) * 100,
					duration: label,
				});
			}
		}
	}
	return gaps;
});

// 3. Refuels
const timelineRefuels = computed(() => {
	if (!props.activeTrack || props.activeTrack.length < 2) return [];
	const refuels = [];
	const FUEL_RISE_THRESHOLD = 5; // Reverted to 5%

	for (let i = 1; i < props.activeTrack.length; i++) {
		const prev = props.activeTrack[i - 1];
		const curr = props.activeTrack[i];

		// Check if fuel exists (note: API might map it to 'fuel', 'fuel_level', 'fuel_level_percentage')
		// LiveMaps should have mapped it to 'fuel' (number).
		if (prev.fuel !== undefined && curr.fuel !== undefined) {
			const rise = curr.fuel - prev.fuel;
			if (rise > FUEL_RISE_THRESHOLD) {
				refuels.push({
					left: (i / props.activeTrack.length) * 100,
					amount: `+${Math.round(rise)}%`,
				});
			}
		}
	}
	return refuels;
});

// 4. Ticks
const timelineTicks = computed(() => {
	if (!props.activeTrack || props.activeTrack.length === 0) return [];

	const ticks: { left: number; time: string; distance: string }[] = [];
	const COUNT = 6; // 0, 20, 40, 60, 80, 100%
	const len = props.activeTrack.length;

	// Identify target indices
	const targetIndices = new Map<number, number>(); // index -> tick_percent (0-5)
	for (let i = 0; i < COUNT; i++) {
		const percent = i / (COUNT - 1);
		const idx = Math.floor(percent * (len - 1));
		targetIndices.set(idx, percent * 100);
	}

	let cumulativeDist = 0;

	// Single pass to calculate distances and capture ticks
	for (let i = 0; i < len; i++) {
		const curr = props.activeTrack[i];

		// Accumulate distance
		if (i > 0) {
			const prev = props.activeTrack[i - 1];
			// Use simple L.latLng if available or helper
			if (curr.lat && curr.lng && prev.lat && prev.lng) {
				cumulativeDist += getDistance(prev.lat, prev.lng, curr.lat, curr.lng);
			}
		}

		// Check if this index is a target tick
		// Note: multiple ticks might map to same index if track is short,
		// effectively we just overwrite or skip?
		// With Set/Map we get unique indices. But we want 6 visual ticks.
		// If track is length 2, indices are 0, 0, 0, 0, 0, 1.
		// We should distinct them or just iterate the COUNT loop?
		// Iterating COUNT approach is better for ensuring 6 ticks, but requires calculating distance up to that point.
	}

	// Re-do: Iterate COUNT, and for each, sum dist up to that index?
	// Optimization: Calculate Cumulative Dist Array? Memory intensive?
	// Optimization: Just do on the fly.

	// Better approach matching indices:
	let runningDist = 0;
	let nextTickIdx = 0;
	// Sort target indices to be safe? They are already sorted by definition.

	// Let's build a map of index -> cumulativeDistance first?
	// No, let's just loop and capture.

	const targets = [];
	for (let i = 0; i < COUNT; i++) {
		targets.push({
			percent: i / (COUNT - 1),
			targetIdx: Math.floor((i / (COUNT - 1)) * (len - 1)),
			captured: false,
		});
	}

	cumulativeDist = 0;
	let currentTargetPtr = 0;

	for (let i = 0; i < len; i++) {
		// Add dist
		if (i > 0) {
			const prev = props.activeTrack[i - 1];
			const curr = props.activeTrack[i];
			if (curr.lat && curr.lng && prev.lat && prev.lng) {
				cumulativeDist += getDistance(prev.lat, prev.lng, curr.lat, curr.lng);
			}
		}

		// While current index >= target index, capture
		while (
			currentTargetPtr < targets.length &&
			i >= targets[currentTargetPtr].targetIdx
		) {
			const t = targets[currentTargetPtr];
			const loc = props.activeTrack[i]; // effectively targetIdx

			let timeStr = "";
			if (loc && loc.timestamp) {
				const ts =
					Number(loc.timestamp) *
					(String(loc.timestamp).length === 10 ? 1000 : 1);
				timeStr = new Date(ts).toLocaleTimeString("it-IT", {
					hour: "2-digit",
					minute: "2-digit",
				});
			}

			ticks.push({
				left: t.percent * 100,
				time: timeStr,
				distance: (cumulativeDist / 1000).toFixed(1) + " km", // e.g. 1.2 km
			});

			currentTargetPtr++;
		}
	}

	return ticks;
});

/* ---------------------------------------------------- */
/*  ROUTE STATS                                         */
/* ---------------------------------------------------- */
const routeStats = computed(() => {
	if (!props.activeTrack || props.activeTrack.length === 0) {
		return { points: 0, distance: "0.00 km", lastPos: "-" };
	}

	// Points
	const points = props.activeTrack.length;

	// Distance
	let distMeters = 0;
	for (let i = 0; i < props.activeTrack.length - 1; i++) {
		const p1 = props.activeTrack[i];
		const p2 = props.activeTrack[i + 1];
		// Simple Haversine or use Leaflet if available in loop,
		// but explicit math is faster than creating L.latLng objects in loop for massive arrays.
		// Let's use simple approximation or helper if we have one.
		// Since we have L loaded, let's try to trust L.latLng for accuracy but might be slow for 2000 points?
		// 2000 is fine.
		distMeters += L.latLng(p1.lat, p1.lng).distanceTo(L.latLng(p2.lat, p2.lng));
	}
	const distance = (distMeters / 1000).toFixed(2) + " km";

	// Last Pos
	const last = props.activeTrack[props.activeTrack.length - 1];
	let lastPos = "-";
	if (last.timestamp) {
		// Timestamp might be seconds or ms. usually seconds in this app based on previous code.
		const ts =
			Number(last.timestamp) *
			(String(last.timestamp).length === 10 ? 1000 : 1);
		lastPos = new Date(ts).toLocaleString("it-IT");
	}

	return { points, distance, lastPos };
});

/* ---------------------------------------------------- */
/*  UNIFIED FOLLOW LOGIC                                */
/* ---------------------------------------------------- */
// This replaces previous scattered watchers for playback/devices
// This replaces previous scattered watchers for playback/devices
watch(
	[lockedDeviceId, () => props.devices],
	() => {
		checkFollow();
	},
	{ deep: true },
);

function checkFollow() {
	if (!lockedDeviceId.value || !map.value) return;
	if (!isFollowing.value) return; // [NEW] Check explicit follow state

	let targetLatLng: [number, number] | null = null;

	// A. Track Mode (Playback)
	// If we are locked on the device that is currently being played back
	if (
		props.activeTrackId &&
		String(lockedDeviceId.value) === String(props.activeTrackId)
	) {
		// Prefer the rendered marker for visual accuracy
		if (playbackMarkerLayer.value) {
			const layers = playbackMarkerLayer.value.getLayers();
			if (layers.length > 0) {
				const marker = layers[0] as any;
				if (marker.getLatLng) {
					const ll = marker.getLatLng();
					targetLatLng = [ll.lat, ll.lng];
				}
			}
		}
		// Fallback to data point
		if (!targetLatLng && props.activeTrack && props.activeTrack.length > 0) {
			const point = props.activeTrack[playbackIndex.value];
			if (point) targetLatLng = [point.lat, point.lng];
		}
	}
	// B. Live Mode
	else {
		const device = props.devices.find((d) => d.id == lockedDeviceId.value);
		if (device) {
			targetLatLng = getDeviceLatLng(device);
		}
	}

	// Apply Pan
	if (targetLatLng) {
		// map.value.panTo(targetLatLng, { animate: true, duration: 0.5 });
		// Use setView for smoother animation loop performance or panTo if user prefers.
		// For playback loop, panTo might lag if duration > interval.
		// Let's use panTo but short duration or just setView if fast?
		// Actually panTo is better for UX.
		map.value.panTo(targetLatLng, { animate: true });
	}
}

// Fit Track
function fitTrack() {
	if (!map.value || !props.activeTrack || props.activeTrack.length === 0)
		return;

	// Collect all points
	const points = props.activeTrack.map(
		(p) => [p.lat, p.lng] as [number, number],
	);
	if (points.length > 0) {
		const bounds = L.latLngBounds(points);
		map.value.fitBounds(bounds, { padding: [50, 50] });
	}
}

/* ---------------------------------------------------- */
/*  PLAYBACK LOGIC                                      */
/* ---------------------------------------------------- */

// Calendar availability logic (Ported from DeviceList)
const activityDates = ref<string[]>([]);
const loadingCalendar = ref(false);

async function fetchMonthActivity(year: number, month: number) {
	if (!props.activeTrackId) return;

	loadingCalendar.value = true;
	activityDates.value = [];

	try {
		const daysInMonth = new Date(year, month + 1, 0).getDate();
		const checks: Promise<string | null>[] = [];

		for (let day = 1; day <= daysInMonth; day++) {
			const start = new Date(year, month, day, 0, 0, 0);
			const end = new Date(year, month, day, 23, 59, 59);
			if (start > new Date()) continue;

			const check = axios
				.get(`/track/${props.activeTrackId}/logs`, {
					params: {
						startTime: Math.floor(start.getTime() / 1000),
						endTime: Math.floor(end.getTime() / 1000),
						limit: 1, // Check for ANY data
					},
					withCredentials: true,
				})
				.then((res) => {
					if (res.data?.logs && res.data.logs.length > 0) {
						return start.toISOString().split("T")[0];
					}
					return null;
				})
				.catch(() => null);

			checks.push(check);
		}

		const results = await Promise.all(checks);
		activityDates.value = results.filter((d): d is string => d !== null);
	} catch (e) {
		console.warn("Failed to fetch activity dates", e);
	} finally {
		loadingCalendar.value = false;
	}
}

// Map Style Toggle
const isSatelliteView = ref(false);
function toggleBaseLayer() {
	if (!map.value) return;

	isSatelliteView.value = !isSatelliteView.value;

	// Remove existing tile layer (assuming it's the first one or we track it)
	// Simpler: Just update the layer URL if we have a reference.
	// Ideally we should track the tileLayer instance.
	// For now, let's just re-add the layer.

	map.value.eachLayer((layer: any) => {
		if (
			layer instanceof L.TileLayer &&
			!layer.options.className?.includes("leaflet-pm")
		) {
			// Heuristic to find base layer
			// Or safer: use a known ref if we stored it (we didn't explicitly store baseLayer ref globally yet)
			// But we know we add one at init.
			map.value?.removeLayer(layer);
		}
	});

	const url = isSatelliteView.value
		? "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
		: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"; // Default

	L.tileLayer(url, {
		attribution: isSatelliteView.value
			? "Tiles &copy; Esri"
			: "&copy; OpenStreetMap contributors",
		maxZoom: 19,
	}).addTo(map.value);
}

function toggleCalendar() {
	showCalendar.value = !showCalendar.value;
	if (showCalendar.value && props.activeTrackId) {
		// Fetch current month
		const now = new Date();
		fetchMonthActivity(now.getFullYear(), now.getMonth());
	}
}

function handleCalendarMonthChange(payload: { year: number; month: number }) {
	if (!props.activeTrackId) return;
	fetchMonthActivity(payload.year, payload.month);
}

function onCalendarSelect(date: string) {
	emit("date-change", date);
	showCalendar.value = false;
}

function onSliderInput() {
	pausePlayback();
	updatePlaybackMarker();
}

function updatePlaybackMarker() {
	// ... (existing track playback logic)
	if (!map.value || !props.activeTrack || !props.activeTrack.length) return;
	if (!playbackMarkerLayer.value) return;

	playbackMarkerLayer.value.clearLayers();

	// ... rest of marker logic
	const point = props.activeTrack[playbackIndex.value];
	if (!point || !point.lat || !point.lng) return;

	// Calculate Bearing
	let angle = 0;
	if (playbackIndex.value < props.activeTrack.length - 1) {
		const next = props.activeTrack[playbackIndex.value + 1];
		if (next.lat && next.lng) {
			angle = calculateBearing(point.lat, point.lng, next.lat, next.lng);
		}
	} else if (playbackIndex.value > 0) {
		const prev = props.activeTrack[playbackIndex.value - 1];
		if (prev.lat && prev.lng) {
			angle = calculateBearing(prev.lat, prev.lng, point.lat, point.lng);
		}
	}

	// Create Rotated Icon
	const icon = createRotatedIcon(angle);

	// Determine Status & Drift Logic
	const rawSpeed = point.speed || 0;
	let speed = rawSpeed < 5 ? 0 : rawSpeed; // Base threshold
	const rpm = point.rpm !== undefined ? point.rpm : 0;

	// DRIFT FILTER: If speed is 0, check if we moved significantly directly
	// Ideally we'd look back. For now:
	// If speed is 0, we FORCE "Parking" and maybe clamp position?
	// Let's implement clamp: find last "moving" or stable point?
	// For simplicity per user request: "indicandoli come parking"

	let statusIcon = "🅿️";
	// let statusText = "Parcheggiato";

	if (speed > 0) {
		statusIcon = "🟢"; // In moto
		// statusText = "In Movimento";
	} else if (speed === 0 && rpm > 0) {
		// ENGINE ON BUT STOPPED -> Idle
		// BUT check user request: "not moving -> parking" for drift?
		// If it's pure drift (0km/h), user wants to see it as stopped.
		statusIcon = "⚠️";
	}

	// FORCE PARKING if speed is 0 to avoid "ghosting" perception as requested
	// "evitando questi falsi e indicandoli come parking"
	if (speed === 0) {
		statusIcon = "🅿️";
	}

	// Format Date/Time
	let dateStr = "";
	let timeStr = "";
	if (point.timestamp) {
		const ts =
			Number(point.timestamp) *
			(String(point.timestamp).length === 10 ? 1000 : 1);
		const d = new Date(ts);
		dateStr = d.toLocaleDateString("it-IT");
		timeStr = d.toLocaleTimeString("it-IT"); // e.g. 13:19:33
	}

	// Label HTML
	const labelContent = `
        <div class="marker-label-content">
            <div class="row-main">
                <span class="icon">${statusIcon}</span>
                <span class="speed">${Math.round(speed)} km/h</span>
            </div>
            <div class="row-sub">
                <span class="date">${dateStr}</span>
                <span class="time">${timeStr}</span>
            </div>
        </div>
    `;

	// Add Marker
	const marker = L.marker([point.lat, point.lng], {
		icon: icon,
		zIndexOffset: 1000,
	}).addTo(playbackMarkerLayer.value);

	marker.bindTooltip(labelContent, {
		permanent: true,
		direction: "top",
		className: "playback-tooltip-custom",
		offset: [0, -20],
	});

	// Sync Follow
	checkFollow();
}

/* ---------------------------------------------------- */
/*  SATELLITE TIMELAPSE LOGIC                           */
/* ---------------------------------------------------- */

const activeSatelliteLayer = computed(() => {
	// Find the first active WMS layer
	// We assume the first one is the "main" one the user wants to animate
	return activeLayers.value.length > 0 ? activeLayers.value[0] : null;
});

const isTimelapsePlaying = ref(false);
let timelapseTimer: ReturnType<typeof setInterval> | null = null;
// TIMELAPSE_INTERVAL moved to top

function toggleTimelapse() {
	isTimelapsePlaying.value = !isTimelapsePlaying.value;
	if (isTimelapsePlaying.value) {
		startTimelapse();
	} else {
		stopTimelapse();
	}
}

function startTimelapse() {
	if (timelapseTimer) clearInterval(timelapseTimer);

	// Immediate next frame
	nextTimelapseFrame();

	timelapseTimer = setInterval(() => {
		nextTimelapseFrame();
	}, TIMELAPSE_INTERVAL);
}

function stopTimelapse() {
	if (timelapseTimer) {
		clearInterval(timelapseTimer);
		timelapseTimer = null;
	}
	isTimelapsePlaying.value = false;
}

function nextTimelapseFrame() {
	if (!activeSatelliteLayer.value) return;
	const layerId = activeSatelliteLayer.value.id;
	const dates = availableDatesMap.value[layerId] || [];
	if (dates.length === 0) return;

	// Sort dates just in case
	const sortedDates = [...dates].sort();

	const currentDate = activeSatelliteLayer.value.date;
	let idx = sortedDates.indexOf(currentDate || "");

	if (idx === -1) {
		// If undefined or not found, start from beginning
		idx = 0;
	} else {
		idx++;
		if (idx >= sortedDates.length) {
			idx = 0; // Loop
		}
	}

	// Set Date
	onLayerDateChange(sortedDates[idx]);
}

function prevTimelapseFrame() {
	if (!activeSatelliteLayer.value) return;
	const layerId = activeSatelliteLayer.value.id;
	const dates = availableDatesMap.value[layerId] || [];
	if (dates.length === 0) return;

	const sortedDates = [...dates].sort();
	const currentDate = activeSatelliteLayer.value.date;
	let idx = sortedDates.indexOf(currentDate || "");

	if (idx === -1) {
		idx = sortedDates.length - 1;
	} else {
		idx--;
		if (idx < 0) {
			idx = sortedDates.length - 1; // Loop
		}
	}

	onLayerDateChange(sortedDates[idx]);
}

function onTimelapseSeek(date: string) {
	if (activeSatelliteLayer.value) {
		onLayerDateChange(date);
	}
}

function togglePlayback() {
	if (isPlaying.value) pausePlayback();
	else startPlayback();
}

// function startPlayback() {
//     if (!props.activeTrack || props.activeTrack.length < 2) return;
//     isPlaying.value = true;
//     lastFrameTime = performance.now();
//     animationFrameId = requestAnimationFrame(animatePlayback);
// }

// let animationFrameId: number | null = null;
let lastUpdateTime = 0;
let accumulatedTime = 0;

function startPlayback() {
	if (!props.activeTrack || props.activeTrack.length < 2) return;

	isPlaying.value = true;

	// Reset if at end
	if (playbackIndex.value >= props.activeTrack.length - 1) {
		playbackIndex.value = 0;
	}

	updatePlaybackMarker();

	lastUpdateTime = performance.now();
	accumulatedTime = 0;
	animationFrameId = requestAnimationFrame(animatePlaybackSmooth);
}

function animatePlaybackSmooth(currentTime: number) {
	if (!isPlaying.value || !props.activeTrack) return;

	const deltaTime = currentTime - lastUpdateTime;
	lastUpdateTime = currentTime;

	// Target frame rate: 60fps, but update marker at controlled rate
	const targetFrameTime = 1000 / 60; // ~16.67ms

	if (deltaTime < targetFrameTime) {
		animationFrameId = requestAnimationFrame(animatePlaybackSmooth);
		return;
	}

	// Calculate time-based progress
	// At 1x speed: 1 point per second = 1000ms per point
	const msPerPointAt1x = 1000 / playbackSpeed.value; // Adjusted for speed

	accumulatedTime += deltaTime;

	// Determine how many points to advance
	const pointsToAdvance = Math.floor(accumulatedTime / msPerPointAt1x);

	if (pointsToAdvance > 0) {
		// Update index
		playbackIndex.value = Math.min(
			playbackIndex.value + pointsToAdvance,
			props.activeTrack.length - 1,
		);

		// Subtract used time
		accumulatedTime -= pointsToAdvance * msPerPointAt1x;

		// Update marker
		nextTick(() => {
			updatePlaybackMarker();
		});
	}

	// Check end
	if (playbackIndex.value >= props.activeTrack.length - 1) {
		pausePlayback();
		return;
	}

	// Continue
	if (isPlaying.value) {
		animationFrameId = requestAnimationFrame(animatePlaybackSmooth);
	}
}

function pausePlayback() {
	console.log("[Playback] Pausing...");

	isPlaying.value = false;

	// Stop animation frame
	if (animationFrameId) {
		cancelAnimationFrame(animationFrameId);
		animationFrameId = null;
	}

	// Reset timing variables
	lastUpdateTime = 0;
	accumulatedTime = 0;

	// Ensure marker is in correct position
	updatePlaybackMarker();

	console.log("[Playback] Paused at index:", playbackIndex.value);
}

// function pausePlayback() {
//     isPlaying.value = false;
//     if (animationFrameId) {
//         cancelAnimationFrame(animationFrameId);
//         animationFrameId = null;
//     }
// }

function restartPlayback() {
	pausePlayback();
	playbackIndex.value = 0;
	updatePlaybackMarker();
	startPlayback();
}

function cycleSpeed() {
	const idx = speedOptions.indexOf(playbackSpeed.value);
	playbackSpeed.value = speedOptions[(idx + 1) % speedOptions.length];
}

function animatePlayback(time: number) {
	if (!isPlaying.value) return;

	const deltaTime = (time - lastFrameTime) / 1000;
	lastFrameTime = time;

	// Advance 1 point per second at 1x
	// To make it smoother we might want to interpolate position, but stepping is safer for now.
	const indexIncrement = playbackSpeed.value * deltaTime * 5; // e.g. 5 samples/sec at 1x

	playbackIndex.value += indexIncrement;

	if (playbackIndex.value >= props.activeTrack!.length - 1) {
		playbackIndex.value = props.activeTrack!.length - 1;
		pausePlayback();
	}

	updatePlaybackMarker();

	if (isPlaying.value) {
		animationFrameId = requestAnimationFrame(animatePlayback);
	}
}

// Re-implement updateDevices if lost
function updateDevices(newDevices: any[]) {
	// This function is intended to be called from outside to update the devices array.
	// The existing watch on `props.devices` already handles updates.
	// If `devices` is a local ref that needs to be updated, uncomment the line below.
	// devices.value = newDevices;
	// Recalculate or re-render markers if needed
	// ... exist logic ...
}

// Expose methods
// [Consolidated defineExpose]
defineExpose({
	centerOnDevice: (device: any) => {
		console.log("[LiveMap] centerOnDevice received:", device);
		lockedDeviceId.value = device.id;
		isFollowing.value = true; // FORCE FOLLOW
		showToast("Follow Attivo", "success");

		if (
			props.activeTrackId &&
			String(device.id) === String(props.activeTrackId)
		) {
			updatePlaybackMarker();
		}

		let latLng = getDeviceLatLng(device);
		if (latLng && map.value) {
			map.value.setView(latLng, 16, { animate: true });
		}
	},
	updateDevices,
	toggleSatellite: () => {
		toggleNDVI();
	},
	toggleLock: (device: any) => {
		if (lockedDeviceId.value === device.id) {
			lockedDeviceId.value = null;
		} else {
			lockedDeviceId.value = device.id;
			const latLng = getDeviceLatLng(device);
			if (map.value && latLng) map.value.panTo(latLng, { animate: true });
		}
	},
	loadDeliveries: (points: any[]) => {
		onDeliveriesLoaded(points);
	},
});

function handleTimelineLock() {
	console.log("handleTimelineLock called");
	console.log("props.activeTrackId:", props.activeTrackId);
	if (
		!props.activeTrackId ||
		!props.activeTrack ||
		props.activeTrack.length === 0
	) {
		console.warn("No valid track to follow");
		toast.warning("Nessun tracciato attivo da seguire");
		return;
	}
	console.log("Current isFollowing before:", isFollowing.value);
	// Toggle Follow State
	isFollowing.value = !isFollowing.value;
	console.log("isFollowing set to after:", isFollowing.value);

	if (isFollowing.value) {
		// Ensure we correspond to active track
		lockedDeviceId.value = props.activeTrackId;
		checkFollow();
		toast.info("Seguo il dispositivo");
	} else {
		toast.info("Auto-follow disattivato");
		// We DO NOT clear lockedDeviceId, so URL/Highlight remains.
	}
}

/* ---------------------------------------------------- */
/*  DELIVERY LOGIC (DEVICE 58)                          */
/* ---------------------------------------------------- */
const deliveryPoints = ref<any[]>([]);
const deliveryMarkersLayer = shallowRef<L.LayerGroup | null>(null);

function onDeliveriesLoaded(points: any[]) {
	// 1. Store points
	deliveryPoints.value = points;

	// 2. Render Markers
	if (!deliveryMarkersLayer.value && map.value) {
		deliveryMarkersLayer.value = L.layerGroup().addTo(map.value);
	}
	deliveryMarkersLayer.value?.clearLayers();

	points.forEach((p) => {
		createDeliveryMarker(p);
	});

	// 3. Zoom to fit
	if (points.length > 0 && map.value) {
		const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
		map.value.fitBounds(bounds, { padding: [50, 50] });
	}
}

function createDeliveryMarker(p: any) {
	if (!deliveryMarkersLayer.value) return;

	const isDelivered = p.status === "delivered";
	const color = isDelivered ? "#4caf50" : "#f44336"; // Green / Red

	const icon = L.divIcon({
		className: "delivery-marker-icon", // Need to add CSS transition:none
		html: `<div style="
            width: 16px; height: 16px; 
            background: ${color}; 
            border: 2px solid white; 
            border-radius: 50%; 
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            display: flex; align-items: center; justify-content: center;
        ">
        ${
					isDelivered
						? '<span style="color:white; font-size:10px;">✓</span>'
						: ""
				}
        </div>`,
		iconSize: [20, 20],
		iconAnchor: [10, 10],
	});

	const marker = L.marker([p.lat, p.lng], { icon }).bindPopup(`
            <div style="text-align:center;">
                <b>Consegna</b><br>
                ${p.address}<br>
                Status: <b>${isDelivered ? "CONSEGNATO" : "DA CONSEGNARE"}</b>
            </div>
        `);

	// Store marker ref on point object for easier update?
	// Or just re-render. Re-render individual is better.
	// Let's attach raw marker to point for fast update
	p._marker = marker;

	deliveryMarkersLayer.value.addLayer(marker);
}

// Watch device 58 position
watch(
	() => props.devices,
	(newDevices) => {
		if (deliveryPoints.value.length === 0) return;

		// Find Device 58
		// Note: ID might be integer 58 or string "58" or mapped name.
		// User said "dispositivo 58". Assuming ID or Name.
		// Let's check logic: usually matched by id.
		const truck = newDevices.find(
			(d) => d.id == DELIVERY_DEVICE_ID || d.name?.includes("58"),
		);

		if (truck) {
			const truckPos = getDeviceLatLng(truck);
			if (truckPos) {
				checkDeliveryProximity(truckPos);
			}
		}
	},
	{ deep: true },
);

function checkDeliveryProximity(truckLatLng: [number, number]) {
	const truckLoc = L.latLng(truckLatLng[0], truckLatLng[1]);
	let changed = false;

	deliveryPoints.value.forEach((p) => {
		if (p.status === "delivered") return;

		const pointLoc = L.latLng(p.lat, p.lng);
		const dist = truckLoc.distanceTo(pointLoc); // meters

		if (dist < DELIVERY_THRESHOLD_METERS) {
			// Point reached
			p.status = "delivered";
			updateDeliveryMarker(p);
			changed = true;
		}
	});
}

function updateDeliveryMarker(p: any) {
	if (p._marker) {
		// Re-create icon or just visual change.
		// Easiest to remove and re-add or setIcon.
		// Let's setIcon.
		const color = "#4caf50";
		const icon = L.divIcon({
			className: "delivery-marker-icon",
			html: `<div style="
                width: 16px; height: 16px; 
                background: ${color}; 
                border: 2px solid white; 
                border-radius: 50%; 
                box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                display: flex; align-items: center; justify-content: center;
            ">
            <span style="color:white; font-size:10px;">✓</span>
            </div>`,
			iconSize: [20, 20],
			iconAnchor: [10, 10],
		});

		p._marker.setIcon(icon);
		p._marker.setPopupContent(`
            <div style="text-align:center;">
                <b>Consegna</b><br>
                ${p.address}<br>
                Status: <b>CONSEGNATO</b>
            </div>
        `);
	}
}
</script>

<style scoped>
.live-map-container {
	width: 100%;
	height: 100%;
	position: relative;
	background: #e5e5e5;
}

.map-view {
	width: 100%;
	height: 100%;
	z-index: 1;
}

.loading-overlay {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	background: rgba(0, 0, 0, 0.7);
	color: white;
	padding: 20px;
	border-radius: 8px;
	z-index: 1000;
}

/* TIMELINE OVERLAY */
.timeline-overlay {
	position: absolute;
	bottom: 20px;
	left: 50%;
	transform: translateX(-50%);
	width: 95%;
	max-width: 900px;

	background: rgba(20, 24, 28, 0.95);
	border: 1px solid rgba(255, 255, 255, 0.08);
	border-radius: 12px;
	padding: 8px 16px; /* Reduced vertical padding */
	z-index: 998;

	display: flex;
	flex-direction: column;
	gap: 8px; /* Tighter gap */
	color: #fff;
	backdrop-filter: blur(10px);
	box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);
}

/* STATS ROW */
.timeline-info-row {
	display: flex;
	justify-content: space-between;
	width: 100%;
	padding-bottom: 4px; /* Minimal padding */
	border-bottom: 1px solid rgba(255, 255, 255, 0.1); /* Subtle border */
	margin-bottom: 2px;
}

.stat-col {
	display: flex;
	flex-direction: column;
	gap: 4px;
}
.stat-col.center {
	flex-direction: row; /* Horizontal buttons */
	align-items: center;
	gap: 12px;
}
.stat-col.right {
	flex-direction: row; /* Horizontal stat groups */
	align-items: center;
}

.stat-label {
	font-size: 0.65rem; /* Minimal */
	color: #888;
	text-transform: uppercase;
	letter-spacing: 0.5px;
}
.stat-value {
	font-size: 0.95rem; /* More elegant, less bulky */
	font-weight: 600;
	color: #fff;
	font-variant-numeric: tabular-nums;
}
.stat-value-row {
	display: flex;
	align-items: center;
}
.timeline-controls-row {
	display: flex;
	align-items: center;
	gap: 12px;
	justify-content: center;
}
.stat-group {
	display: flex;
	flex-direction: column;
	align-items: flex-end; /* right align for right col */
}
/* Adjust layout for stat-col to handle grouping */
.stat-col.right {
	display: flex;
	gap: 16px;
	align-items: center;
	justify-content: flex-end;
}

.lock-btn {
	background: transparent;
	border: 1px solid transparent; /* Cleaner default */
	color: #999;
	border-radius: 6px;
	padding: 4px 8px;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.2s;
	font-size: 1.1rem;
}
.lock-btn:hover {
	background: rgba(255, 255, 255, 0.05);
	color: #fff;
}
.lock-btn.active {
	background: rgba(33, 150, 243, 0.15); /* Subtle blue bg */
	color: #64b5f6; /* Lighter blue text */
	border: 1px solid rgba(33, 150, 243, 0.3);
}

.timeline-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	font-size: 0.9rem;
	color: #ddd;
	position: relative;
	height: 30px;
}

.timeline-meta {
	display: flex;
	align-items: center;
	gap: 8px;
	font-weight: 500;
}

.calendar-btn {
	background: none;
	border: 1px solid #444;
	border-radius: 6px;
	cursor: pointer;
	padding: 4px 8px;
	font-size: 1.1rem;
	transition: 0.2s;
}
.calendar-btn:hover {
	background: #333;
	border-color: #666;
}

.timeline-stats span {
	background: #00b200;
	color: #fff;
	padding: 2px 8px;
	border-radius: 4px;
	font-weight: bold;
	font-size: 0.85rem;
}

.zoom-warning-btn {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	background: #ffc107;
	color: #000;
	padding: 12px 24px;
	border-radius: 30px;
	font-size: 1rem;
	font-weight: 600;
	z-index: 3000;
	box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
	border: none;
	cursor: pointer;
	transition: transform 0.2s, box-shadow 0.2s;
	display: flex;
	align-items: center;
	gap: 8px;
	animation: fadeIn 0.3s ease-out;
}

.zoom-warning-btn:hover {
	transform: translate(-50%, -50%) scale(1.05);
	box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
	background: #ffca28;
}

@keyframes fadeIn {
	from {
		opacity: 0;
		transform: translate(-50%, -10px);
	}
	to {
		opacity: 1;
		transform: translate(-50%, 0);
	}
}
.close-track-btn {
	background: none;
	border: none;
	font-size: 1.2rem;
	cursor: pointer;
	opacity: 0.7;
	transition: 0.2s;
}
.close-track-btn:hover {
	opacity: 1;
	color: #f44336;
}

/* TIMELINE UI (Match RouteMap) */
.timeline-visual-wrapper {
	position: relative;
	width: 100%;
	height: 70px;
	margin-top: 10px;
}

.timeline-bar {
	position: absolute;
	top: 30px;
	left: 0;
	width: 100%;
	height: 8px;
	background: #333;
	border-radius: 4px;
	overflow: hidden;
	display: flex;
	z-index: 1;
}

.timeline-gaps,
.timeline-refuels {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	pointer-events: none;
	z-index: 5;
}

.gap-marker {
	position: absolute;
	top: 5px; /* Above bar (bar at 30px) */
	transform: translateX(-50%);
	display: flex;
	flex-direction: column;
	align-items: center;
}
.gap-line {
	width: 2px;
	height: 35px; /* Goes down through bar */
	background: #f44336;
	opacity: 0.6;
}
.gap-label {
	background: #f44336;
	color: white;
	font-size: 0.65rem;
	padding: 1px 3px;
	border-radius: 2px;
	margin-bottom: 2px;
}

.refuel-marker {
	position: absolute;
	top: -25px; /* Revert to floating above */
	transform: translateX(-50%);
	display: flex;
	flex-direction: column;
	align-items: center;
	pointer-events: auto;
	z-index: 10;
}
.refuel-icon {
	font-size: 1.2rem;
	filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5));
	animation: bounce 1s infinite;
}
.refuel-label {
	font-size: 0.7rem;
	background: #4caf50;
	color: white;
	padding: 1px 4px;
	border-radius: 4px;
	margin-top: -2px;
}

@keyframes bounce {
	0%,
	100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-3px);
	}
}

.timeline-slider {
	position: absolute;
	top: 24px; /* Centered on bar (30px + 4px) */
	left: -1%;
	width: 102%;
	height: 20px;
	z-index: 20;
	cursor: pointer;
	opacity: 0; /* Hidden track, custom thumb handled if needed */
}
.timeline-slider:hover {
	opacity: 1; /* Show on hover maybe? Or keep hidden track */
}
/* Revert slider styles for visibility during debug if needed */
.timeline-slider {
	opacity: 1;
	background: transparent;
	appearance: none;
	-webkit-appearance: none;
}
.timeline-slider::-webkit-slider-thumb {
	-webkit-appearance: none;
	height: 16px;
	width: 16px;
	border-radius: 50%;
	background: white;
	border: 2px solid #2196f3;
	cursor: grab;
	margin-top: -6px; /* Adjust for track height if needed */
}

.status-segment {
	height: 100%;
}
.status-segment.moving {
	background: #4caf50;
}
.status-segment.idle {
	background: #ffeb3b;
}
.status-segment.parking {
	background: #2196f3;
}

/* SLIDER */
.timeline-slider {
	position: absolute;
	bottom: 22px; /* Overlap bar */
	left: 0;
	width: 100%;
	height: 12px;
	margin: 0;
	padding: 0;
	z-index: 10;
	opacity: 0; /* Hidden but clickable */
	cursor: grab;
}

.timeline-slider:active {
	cursor: grabbing;
}

/* Custom visual thumb if needed, but native is hidden. 
   We essentially drag over the bar. 
   To make it usable we might need opacity. 
   Let's use a standard slider look but transparent track.
*/
.timeline-slider {
	opacity: 1;
	-webkit-appearance: none; /* Override default CSS styles */
	appearance: none;
	background: transparent;
}
.timeline-slider::-webkit-slider-thumb {
	-webkit-appearance: none;
	appearance: none;
	width: 16px;
	height: 24px;
	background: #fff;
	border: 2px solid #2196f3;
	border-radius: 4px;
	cursor: pointer;
	margin-top: -6px; /* center */
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

/* RULER */
.timeline-ruler {
	position: absolute;
	top: 30px; /* Same as bar top */
	left: 0;
	width: 100%;
	height: 100%;
	pointer-events: none;
}
.ruler-tick {
	position: absolute;
	transform: translateX(-50%);
	top: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
}
.tick-mark {
	width: 1px;
	height: 14px;
	background: rgba(255, 255, 255, 0.3);
	margin-top: -3px;
}
.tick-time {
	position: absolute;
	top: -15px;
	font-size: 10px;
	color: #bbb;
	white-space: nowrap;
}
.tick-dist {
	position: absolute;
	bottom: -15px; /* Below the tick mark */
	font-size: 9px;
	color: #888;
	white-space: nowrap;
}

/* EXTRA MARKERS (GAPS, REFUELS) */
.timeline-gaps,
.timeline-refuels {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	pointer-events: none;
	z-index: 10; /* Ensure above timeline bar */
}

.gap-marker {
	position: absolute;
	top: -15px; /* Revert to negative to float above */
	transform: translateX(-50%);
	display: flex;
	flex-direction: column;
	align-items: center;
}
.gap-line {
	width: 2px;
	height: 35px;
	background: #f44336;
	opacity: 0.6;
}
.gap-label {
	background: #f44336;
	color: white;
	font-size: 0.65rem;
	padding: 1px 3px;
	border-radius: 2px;
	white-space: nowrap;
}

/* MAP CONTROLS
   position: fixed garantisce visibilità sopra l'iframe Windy (che il browser
   compositor può renderizzare su un layer GPU separato, ignorando z-index).
   transform: translateZ(0) promuove l'elemento su un layer GPU dedicato,
   assicurando che il compositor lo renda sopra il layer GPU dell'iframe.
   left usa --sb-offset (ereditata da .page-wrap in App.vue) per restare
   allineati al bordo sinistro della mappa anche quando la sidebar si apre. */
.map-controls-overlay {
	position: fixed;
	top: 76px; /* 56px (header) + 20px */
	left: calc(var(--sb-offset, 60px) + 20px);
	z-index: 2001;
	display: flex;
	flex-direction: row;
	gap: 12px;
	align-items: center;
	transition: left 0.25s ease;
	transform: translateZ(0);
}

.layer-btn {
	width: 32px;
	height: 32px;
	border: 1px solid #ccc;
	background: white;
	border-radius: 6px;
	cursor: pointer;
	font-size: 1.2rem;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: 0.2s;
}

.layer-btn:hover {
	background: #f0f0f0;
}
.layer-btn.active {
	background: #e3f2fd;
	border-color: #2196f3;
}

.map-control-btn {
	width: 40px;
	height: 40px;
	background: white;
	border: 2px solid rgba(0, 0, 0, 0.2);
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 1.5rem;
	cursor: pointer;
	box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
	transition: 0.2s;
}

.map-control-btn:hover {
	background: #f5f5f5;
}

.map-control-btn.active {
	background: #e8f5e9;
	border-color: #4caf50;
	box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* MOBILE OPTIMIZATION */
@media (max-width: 768px) {
	.map-controls-overlay {
		flex-direction: column;
		left: 15px; /* Adjust as needed for mobile */
		top: 76px; /* 56px + 20px */
		gap: 8px;
	}

	/* Force Leaflet controls (Zoom, Geoman, etc) next to our overlay */
	:deep(.leaflet-top.leaflet-left) {
		left: 65px !important; /* Move it to the right of our column (40px + margin) */
		top: 10px !important;
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	/* Adjust zoom control specific spacing if needed */
	:deep(.leaflet-control-zoom) {
		margin-left: 0 !important;
		margin-top: 19px !important;
	}
}

/* DESKTOP OPTIMIZATION - Geoman near Zoom */
@media (min-width: 769px) {
	:deep(.leaflet-top.leaflet-left) {
		left: auto !important;
		right: 170px !important; /* Positioned near the zoom indicator */
		top: 5px !important;
		transition: right 0.25s ease;
	}
}

/* CONTROLS ROW */
.playback-controls {
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 16px;
	padding-top: 4px;
}
.control-btn {
	background: #333;
	border: 1px solid #444;
	color: #eee;
	width: 36px;
	height: 36px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	font-size: 1.2rem;
	transition: all 0.2s;
}
.control-btn:hover {
	background: #444;
	border-color: #666;
	transform: scale(1.05);
}
.control-btn.playing {
	background: rgba(33, 150, 243, 0.2);
	border-color: #2196f3;
	color: #2196f3;
}
.speed-btn {
	width: auto;
	padding: 0 12px;
	border-radius: 18px;
	font-size: 0.85rem;
	font-weight: 600;
}

/* CALENDAR POPOVER */
.calendar-popover {
	position: absolute;
	bottom: 50px;
	left: 20px;
	z-index: 2000;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
	background: #1e1e1e;
	border-radius: 12px;
}
.zoom-level-indicator {
	position: absolute;
	top: 16px;
	right: 60px; /* Left of the zoom controls (which are usually right: 10px + width) */
	z-index: 1000;
	background: rgba(20, 24, 28, 0.9);
	backdrop-filter: blur(4px);
	border-radius: 4px;
	padding: 6px 10px;
	color: white;
	font-size: 0.7rem; /* Smaller font as requested */
	font-weight: 500;
	box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
	border: 1px solid rgba(255, 255, 255, 0.1);
	pointer-events: none; /* Let clicks pass specific area if needed, but mainly informational */
}

@media (max-width: 768px) {
	.zoom-level-indicator {
		top: 20px;
	}
}

/* TIMELAPSE TOGGLE */
.timelapse-toggle-overlay {
	position: absolute;
	top: 70px;
	left: 20px;
	z-index: 999;
}

/* GEOMAN TOOLBAR CUSTOMIZATION */
:deep(.leaflet-top.leaflet-left) {
	top: 5px;
	left: calc(50% + 180px); /* Centers (340px / 2 = 170) plus gap */
	right: auto;
	pointer-events: none; /* Let clicks pass through container gaps */
	display: flex; /* Align toolbars side-by-side */
	gap: 10px;
	align-items: flex-start;
}

:deep(.leaflet-pm-toolbar) {
	display: flex;
	flex-direction: row; /* Horizontal buttons */
	pointer-events: auto; /* Re-enable clicks */
	/* Remove custom dark styles to revert to "previous style" (White) */
}

/* Adjust button internal borders for horizontal layout */
:deep(.leaflet-pm-toolbar .leaflet-buttons-control-button) {
	/* Keep default dimensions but ensure they flow horizontally */
	float: none; /* Leaflet buttons often float */
}

/* Fix borders for horizontal bar: Remove bottom borders, add right borders */
:deep(.leaflet-bar a) {
	border-bottom: none;
	border-right: 1px solid #ccc;
}
:deep(.leaflet-bar a:last-child) {
	border-right: none;
	border-bottom: none;
}

:deep(.leaflet-pm-actions-container) {
	position: absolute;
	top: 100%; /* Show below */
	left: 0;
	width: max-content;
	background: #fff; /* Default white for actions */
	border: 1px solid #ccc;
	border-radius: 4px;
	margin-top: 4px;
}
:deep(.leaflet-pm-action) {
	color: #333; /* Default text color */
}
:deep(.leaflet-pm-action:hover) {
	background-color: #f4f4f4;
}

/* FIX: Prevent markers from floating during zoom */
:deep(.leaflet-marker-icon),
:deep(.leaflet-marker-shadow),
:deep(.delivery-marker-icon) {
	transition: none !important;
}

.layer-btn {
	width: 36px;
	height: 36px;
	border: none;
	background: transparent;
	font-size: 1.2rem;
	cursor: pointer;
	border-radius: 4px;
	transition: background 0.2s;
}

.layer-btn:hover {
	background: #f0f0f0;
}

.layer-btn.active {
	background: #e3f2fd;
	border: 1px solid #2196f3;
}

/* [FIX] Map Container Size */
.map-container {
	width: 100%;
	height: 100%;
	position: relative;
	z-index: 1;
}

/* Windy Control Group */
.control-group-column {
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
}

.windy-layer-selector {
	position: absolute;
	top: 48px;
	left: 50%;
	transform: translateX(-50%);
	z-index: 2005;
	display: flex;
	flex-direction: column;
	gap: 6px;
	background: rgba(255, 255, 255, 0.95);
	padding: 8px;
	border-radius: 8px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
	align-items: center;
}

.layer-separator {
	width: 100%;
	border: none;
	border-top: 1px solid #e0e0e0;
	margin: 0;
}


/* Copertura Radio legend — sc-19255 */
.coverage-legend {
	position: absolute;
	top: 48px;
	right: 52px;
	width: max-content;
	background: rgba(255, 255, 255, 0.96);
	padding: 8px 10px;
	border-radius: 8px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
	z-index: 2005;
	font-size: 0.75rem;
	color: #2c3e50;
	text-align: left;
}
.coverage-legend-title {
	font-weight: 700;
	margin-bottom: 6px;
}
.coverage-legend-row {
	display: flex;
	align-items: center;
	gap: 6px;
	margin-bottom: 3px;
	white-space: nowrap;
}
.cov-swatch {
	width: 14px;
	height: 14px;
	border-radius: 3px;
	display: inline-block;
}
.cov-dead {
	background: rgba(231, 76, 60, 0.55);
}
.cov-weak {
	background: rgba(243, 156, 18, 0.45);
}
.coverage-legend-hint {
	margin-top: 4px;
	font-size: 0.68rem;
	color: #7f8c8d;
	font-style: italic;
}

/* Traffic Density Clear Button */
.traffic-clear-btn {
	width: 40px;
	height: 40px;
	border: 2px solid rgba(231, 76, 60, 0.3);
	background: rgba(255, 255, 255, 0.95);
	color: #e74c3c;
	font-size: 1.5rem;
	font-weight: bold;
	border-radius: 8px;
	cursor: pointer;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	justify-content: center;
}

.traffic-clear-btn:hover {
	background: #e74c3c;
	color: white;
	transform: scale(1.1);
	box-shadow: 0 4px 12px rgba(231, 76, 60, 0.4);
}

.traffic-clear-btn:active {
	transform: scale(0.95);
}
</style>
